/* ─────────────────────────────────────────────────────────────────────────────
   EIGHT CART — the storefront cart drawer engine.

   ONE file, two homes:
     · the theme (uploaded as an asset by the Eight Storefront app extension), where
       it runs against the real Shopify Ajax Cart API;
     · the OS cockpit preview, where it runs against a fake cart object.

   That is deliberate. Upcart's editor preview is a separate re-implementation of its
   drawer, which is why a preview can look right while the live cart looks wrong. Here
   the preview IS the drawer — same render(), same CSS — so what the owner approves in
   the cockpit is byte-for-byte what a customer gets.

   It talks to Shopify only through the documented Ajax endpoints (/cart.js,
   /cart/add.js, /cart/change.js, /cart/update.js) and hands off to /checkout. It never
   calls our own server: the settings are baked into the page by Liquid, so a cart keeps
   working even when the OS box is down.

   No dependencies, no build step, ES5-conservative on purpose — it has to run on
   whatever browser a customer brings.
   ───────────────────────────────────────────────────────────────────────────── */
(function (window, document) {
  "use strict";

  if (window.EightCart) return;              // an app-embed AND a snippet on one store: first wins

  /* ── Defaults ───────────────────────────────────────────────────────────────
     Every key the editor can write. A config from the shop metafield is merged
     ON TOP of this, so a cart built by an older version of the editor never ends
     up with an undefined branch — it just uses the default for whatever is new. */
  var DEFAULTS = {
    enabled: false,

    design: {
      inherit_fonts: true,
      strikethrough: true,
      subtotal_line: false,
      // Which edge the drawer slides in from. Upcart calls it cartPosition; on a shop whose
      // cart icon sits top-left, a drawer arriving from the right crosses the whole screen.
      side: "right",                         // right | left
      // WHICH UPCART CARD. Upcart ships two, and both are live on this estate: v2 (Watson &
      // Carter) puts the options on one line and the price above the quantity box; v1 (Harper
      // & Wells, Grant & Mercer, Van Leeuwen) stacks the options and puts the price to the
      // RIGHT of the quantity box, with a heavier title. They differ in a dozen measurements,
      // not one, so this is a single switch rather than a pile of them — exactly as Upcart
      // treats it. Read it off a store with: upcartEditorSettings.cartItemsModuleVersion.
      // v2n = v2 as Upcart draws it since September 2026 (Wilson & Carter, measured 2026-09-25):
      // same layout, but Upcart's own bin, drawn stepper marks with #ddd dividers, a typed
      // quantity and a plain hyphen before the discount. Kept apart so the v2 stores that were
      // matched in August do not move a pixel.
      card: "v2",                            // v2 | v2n | v1
      width_desktop: "default",              // narrow | default | wide
      width_mobile: "full",                  // default | full
      bg: "#FFFFFF",
      accent: "#f6f6f7",                     // the soft panel tint behind rows/pills
      text: "#000000",
      savings: "#c6a661",
      btn_radius: 0,
      btn_color: "#c6a661",
      btn_text: "#FFFFFF",
      // 4, not 0. Upcart's corner-radius SETTING only governs buttons; its quantity stepper
      // is rounded at 4px no matter what, and copying the button value onto it gave us a
      // hard-cornered box next to their soft one.
      input_radius: 4,
      img_radius: 0,
      img_ratio: "square",                   // square | portrait | natural
      btn_text_hover: "",                    // blank = same as btn_text
      subtotal_color: "",
      // The three colours I had baked into the stylesheet while matching Upcart pixel for
      // pixel. Upcart does not expose them either — but "it can be configured" has to be
      // true without an asterisk, and on a store whose accent is white (Harper & Wells) a
      // fixed tint is the difference between a visible chip and none.
      line_color: "",                        // blank = a 7% tint of the text colour
      chip_bg: "",                           // blank = a 5% tint
      chip_text: "",                         // blank = #585858
      input_border: "",                      // blank = a 20% tint                    // blank = inherit the cart text colour
      continue_shopping: false,              // a second, quiet way out of the drawer
      continue_text: "Continue shopping"
    },

    header: {
      align: "center",                       // left | center
      title: "",                             // blank = use the store logo, like Upcart
      logo: "",
      logo_size: 34,                         // px, matches Upcart's logo-size slider
      show_count: false,
      sticky: true,
      height: "tall",                        // slim | tall
      border_bottom: "thin",                 // none | thin | thick
      bg: "",                                // blank = the cart background
      // The close button gets its own set because Upcart gives it six, and a cart that
      // cannot match a brand's close button is a cart that looks borrowed.
      close_side: "right",                   // left | right
      close_bg: "",                          // blank = the accent tint
      close_bg_hover: "",
      close_icon_color: "",                  // blank = the cart text colour
      close_icon_hover: "",
      close_icon_size: "medium",             // small | medium | large
      close_icon_thickness: "normal",        // normal | thick
      close_border: "none",                  // none | thin | normal | thick
      close_border_color: "#000000"
    },

    announcements: {
      enabled: false,
      position: "top",                       // top | bottom
      items: [],                             // [{text, link}]
      rotate_ms: 4000,
      bg: "#111111",
      color: "#FFFFFF",
      border_color: "",                      // blank = no border
      height: "normal",                      // slim | normal | thick
      font_size: 13,
      // A countdown, in seconds. Upcart writes it MM:SS and drops it into the text with
      // {TIMER}. 0 = no clock. It is theatre, so it resets on every page load rather than
      // pretending to hold a real reservation — a fake deadline that "expires" and changes
      // nothing teaches customers to ignore you.
      countdown: 0
    },

    rewards: {
      // Off until the merchant has actually created the matching Shopify discount. A bar
      // that promises free shipping the checkout does not give is worse than no bar.
      enabled: false,
      // Upcart drives this bar off ONE of two things, and the live Grant & Morgan cart uses
      // the second: how much is in the cart, or HOW MANY ITEMS are in it ("buy 2, get 10%").
      // Building only the money version would have quietly dropped the setup that is
      // actually running on the stores.
      basis: "cartTotal",                    // cartTotal | itemCount
      // Each tier: {amount (store currency, for cartTotal), count (items, for itemCount),
      //             type, value, text, before, code}
      // type: free_shipping | percent | fixed | gift
      tiers: [],
      // {AMOUNT} / {COUNT} are Upcart's own placeholders, kept so wording can be pasted
      // across from the old cart unchanged. {{remaining}} / {{reward}} also still work.
      progress_text: "You're {AMOUNT} away from {{reward}}",
      progress_text_count: "You're {COUNT} product away from {{reward}}",
      // Upcart bolds the number only when the store's own text says <b>{COUNT}</b>. Harper
      // & Wells' text does not, so there the "1" stays plain (measured 2026-09-24).
      bold_count: true,
      achieved_text: "You've unlocked {{reward}}!",
      bar_color: "#c6a661",
      track_color: "#e9e9ec",
      icon: "tag",                           // tag | truck | gift | star
      icon_done: "",                         // blank = the bar colour
      icon_todo: "",                         // blank = the track colour
      // A single-tier bar with one icon parked at 100% looks like an unfinished progress
      // bar. Upcart hides it by default for exactly one tier, and so do we.
      single_icon: false,
      // A gift tier can name a product. Linking it lets the customer see what they are
      // working towards instead of guessing from four words.
      product_link: false,
      show_on_empty: false,
      // Measure against the price BEFORE discounts. Without this a customer who already
      // earned 20% off slides backwards down the bar the moment the discount applies —
      // the reward punishing itself.
      pre_discount: false
    },

    // The row that shows what Shopify has already taken off this cart. No on/off switch: a
    // shop that hides the discount it just granted is a shop arguing with its own checkout.
    // Only the word stays configurable, because it is read by Dutch, French and Spanish
    // customers. The name of the discount is always shown next to it.
    discounts: {
      label: "Discounts"
    },

    // How a cart line is drawn. Not a panel either — these are the cart, not options about it.
    items: {
      // Nothing here about how a line LOOKS. It used to carry six switches — show the
      // variant, show the vendor, show properties, hide the underscored ones, box or pill
      // stepper, allow removing — and all thirteen stores had them at exactly these values,
      // because there is only one right answer to each. A switch nobody moves is a question
      // the owner has to answer every time he opens the panel. The variant, the properties
      // and the remove button are always drawn, app bookkeeping (_bundleId) is always
      // hidden, the vendor never is, and the stepper is a box.
      empty_text: "Your cart is empty",
      empty_cta: "Continue shopping",
      empty_cta_link: "/collections/all"
    },

    codes: {
      enabled: false,
      label: "Discount code",
      placeholder: "Discount code",
      button: "Apply"
    },

    // No footnote. "Taxes and shipping calculated at checkout" was here because Upcart puts
    // it there; the owner never wants it. Under the checkout button there is either a trust
    // badge or nothing at all. The savings row went with it: it only ever appeared when the
    // discount row was switched off, and that switch no longer exists.
    summary: {
      checkout_text: "Checkout",
      show_total_on_button: true
    },

    trust: {
      // Upcart takes a single uploaded badge STRIP (one PNG with all the card logos on it)
      // and shows it under the checkout button. That is what Grant & Morgan actually runs,
      // and it beats drawing our own logos: real card marks are trademarked artwork, and a
      // hand-rolled "VISA" in a box looks exactly as cheap as it is.
      enabled: false,
      text: "",
      image: "",
      position: "bottom"                     // bottom | above_checkout
    },

    // SHIPPING PROTECTION — Upcart's add-on, built the way Upcart builds it: a row with an
    // On/Off switch just above the checkout button, and behind the switch a REAL product in
    // the shop (one variant per price) that goes in and out of the cart like any other line.
    // Only that line is never DRAWN as a line — it is the switch. Because the money is
    // Shopify's from the first second, the checkout, the order, the P&L and a refund all see
    // an ordinary line item, and nothing here has to reconcile anything afterwards.
    protection: {
      enabled: false,
      title: "Shipping Protection",
      description: "Protect your order from damage, loss, or theft during shipping.",
      image: "",                             // blank = a shield in the cart's own colours
      pricing: "flat",                       // flat | tiers
      price: 3.95,                           // store currency, flat mode
      // Upcart's "dynamic pricing": each row covers cart values up to and including its
      // limit; the row with no limit covers everything above the last one.
      tiers: [],                             // [{upto, price}]  (store currency)
      pre_discount: false,                   // judge the tier on the goods BEFORE discounts
      // On by itself the moment the first item lands; the customer can switch it off, and
      // stays off for the rest of the visit. ON by default: the owner's call (2026-09-14,
      // "het moet voorgevinkt zijn"), made with the tradeoff named — a pre-ticked paid extra
      // is unlawful in the EU (Consumer Rights Directive art. 22) and Upcart withdrew its own
      // version in 2025 on Shopify's demand. Per store it is one tick to turn off.
      preselect: true,
      count_in_items: false,                 // the header and the badge count goods, not cover
      // Upcart's switch is green when on whatever the button colour is (read off their own
      // recording: teal-green track, white knob, a white tick on the track). Blank = that green.
      switch_color: "",
      // Upcart's "Auto-fulfill shipping protection": the cover line is marked fulfilled the
      // moment every shippable line on the order is, so an order never sits on "partially
      // fulfilled" for a product nobody could ever ship. Done by the OS's order sync.
      autofulfill: true,
      // Written by the cockpit's "Create or update the product in Shopify" action, never by
      // hand: which product, and which variant carries which price. The drawer only ever
      // charges what is listed HERE — a price typed above that was never pushed to Shopify
      // is not a price the customer can be asked to pay.
      product_id: "",
      handle: "",
      variants: []                           // [{id, price (cents), upto (cents | null)}]
    },

    sticky: {
      enabled: false,
      position: "bottom-right",              // bottom-right | bottom-left | middle-right
      show_count: true,
      hide_when_empty: true,
      size: 54,
      bg: "",                                // blank = the button colour
      icon_color: "",                        // blank = the button text colour
      icon_style: "bag",                     // bag | cart | basket
      badge_bg: "",                          // blank = the cart accent colour
      badge_text: "",                        // blank = the cart text colour
      devices: "both"                        // both | desktop | mobile
    },

    // Every word the drawer says, in one place. These were hardcoded English, which is
    // indefensible on an estate that sells in Chile, the Netherlands and Germany: a Spanish
    // shop was literally printing "(Save $36.00)" under a Spanish product name. The engine
    // now says nothing it was not told to say.
    text: {
      save: "(Save {AMOUNT})",
      subtotal: "Subtotal",
      free_shipping: "FREE shipping",
      percent_off: "{VALUE}% off",
      amount_off: "{AMOUNT} off",
      code_invalid: "That code is not valid",
      free_gift: "a free gift",
      reward: "a reward",
      all_unlocked: "All rewards unlocked!",
      // Screen-reader labels. Invisible, but a Dutch shop read aloud in English is still
      // a Dutch shop being read aloud in English.
      aria_cart: "Shopping cart",
      aria_close: "Close cart",
      aria_open: "Open cart",
      aria_remove: "Remove",
      aria_less: "Decrease quantity",
      aria_more: "Increase quantity",
      aria_qty: "Product quantity"
    },

    // Upcart lets you override its templates in JSX. That is their whole rendering layer;
    // reimplementing it would cost weeks and buy little. These two do the same job for a
    // tenth of the work: your own CSS, and HTML dropped at the points that matter.
    custom: {
      css: "",
      // The sticky button lives OUTSIDE the drawer, so drawer CSS cannot reach it.
      css_sticky: "",
      html: {
        above_header: "", below_header: "", between_items: "",
        above_footer: "", above_checkout: "", below_checkout: "", bottom: "", empty: ""
      }
    },

    behaviour: {
      open_on_add: true,
      intercept_cart_link: true,
      replace_cart_page: false,
      close_on_backdrop: true,
      // Theme drawers that would otherwise open next to ours. The sweep in
      // sweepThemeDrawers() catches what this list misses; these are the ones worth naming
      // because they are exact and take effect before a single frame is painted.
      // Covers Dawn and the OS 2.0 defaults, plus the premium themes in use across the
      // eight stores (Impulse, Prestige, Motion, Warehouse, Ella, Shrine, Symmetry).
      hide_selectors: [
        "cart-drawer", "cart-notification", "#CartDrawer", "#cart-drawer", ".cart-drawer",
        ".drawer--cart", ".drawer.drawer--right", ".mini-cart", "#mini-cart", "#slidecart",
        ".js-drawer-open", "#CartPopover", ".cart-popover", ".cart-sidebar", "#sidebar-cart",
        ".ajaxcart", "#ajaxifyDrawer", ".header__cart-drawer", "[data-cart-drawer]",
        ".cart-flyout", "#cart-flyout", ".slide-cart", ".t4s-drawer--cart"
      ]
    }
  };

  /* ── Small helpers ─────────────────────────────────────────────────────────── */

  function isObj(v) { return v && typeof v === "object" && !(v instanceof Array); }

  // Deep-merge `src` over a CLONE of `base`. Arrays replace wholesale — a tier list
  // of two must never merge into a default list of three and come back as three.
  function merge(base, src) {
    var out = {}, k;
    for (k in base) if (Object.prototype.hasOwnProperty.call(base, k)) {
      out[k] = base[k] instanceof Array ? base[k].slice() : (isObj(base[k]) ? merge(base[k], {}) : base[k]);
    }
    if (!isObj(src)) return out;
    for (k in src) if (Object.prototype.hasOwnProperty.call(src, k)) {
      if (isObj(out[k]) && isObj(src[k])) out[k] = merge(out[k], src[k]);
      else if (src[k] !== undefined && src[k] !== null) out[k] = src[k] instanceof Array ? src[k].slice() : src[k];
    }
    return out;
  }

  // Everything a merchant types goes through this before it reaches innerHTML.
  function esc(s) {
    return String(s === null || s === undefined ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  // A merchant-supplied URL must not become `javascript:`. Relative and http(s) only.
  function safeUrl(u) {
    u = String(u || "").trim();
    if (!u) return "";
    if (/^(https?:)?\/\//i.test(u) || u.charAt(0) === "/" || u.charAt(0) === "#") return esc(u);
    return "";
  }

  // Same rule for an <img src>, plus inline images. `data:image/…` is safe in an img src —
  // an SVG loaded that way cannot run script — and it is what the cockpit preview uses for
  // its placeholders. Everything else still has to be a real link, and `data:text/html`
  // never gets through, which is the case that actually matters.
  function safeImg(u) {
    u = String(u || "").trim();
    if (/^data:image\/(png|jpe?g|gif|webp|svg\+xml)[;,]/i.test(u)) return esc(u);
    return safeUrl(u);
  }

  /* Merchant-authored HTML, dropped into the cart. Scripts and inline handlers are
     stripped — not because the owner is not trusted, but because this markup rides on
     every page of a live storefront next to a checkout button. If an OS login is ever
     stolen, the difference between "they changed some wording" and "they injected a card
     skimmer into nine shops" is exactly this filter. Everything visual still works. */
  function safeHtml(h) {
    return String(h || "")
      .replace(/<\s*script[\s\S]*?<\s*\/\s*script\s*>/gi, "")
      .replace(/<\s*script[^>]*>/gi, "")
      .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
      .replace(/(href|src)\s*=\s*("|')?\s*javascript:/gi, "$1=$2");
  }

  function slot(name) {
    var h = ((CFG.custom || {}).html || {})[name];
    return h ? '<div class="ec-slot ec-slot--' + name + '">' + safeHtml(h) + "</div>" : "";
  }

  function el(html) {
    var d = document.createElement("div");
    d.innerHTML = String(html).trim();
    return d.firstChild;
  }

  /* ── Money ──────────────────────────────────────────────────────────────────
     Shopify hands the theme a money_format like `${{amount}}` or
     `€{{amount_with_comma_separator}}`. Reimplemented here rather than borrowed from
     the theme, because the theme's copy is not guaranteed to exist. */
  function pad(n) { return n < 10 ? "0" + n : String(n); }

  function fx(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents === null) cents = 0;
    var num = (cents / 100).toFixed(precision);
    var parts = num.split(".");
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
    return precision && parts[1] ? whole + decimal + parts[1] : whole;
  }

  /* Shopify's legacy `shop.money_format` is NOT what the rest of the storefront prints.
     LUMA proved it: the theme rendered $79.990 (Chilean dots) while money_format still said
     `${{amount_no_decimals}}` → $79,990. A cart that disagrees with the product page about
     the price is worse than one that looks plain.

     So we stop guessing and let Shopify format two known amounts for us — 1000.00 and 1.23,
     printed through its own `money` filter by the snippet — and read the pattern back off
     them: prefix, suffix, thousands separator, decimal separator, how many decimals. That is
     correct on every store and every currency without a table here to maintain. */
  function _mfClean(x) {
    return String(x || "").replace(/<[^>]*>/g, "")
      .replace(/&nbsp;|&#160;/gi, " ").trim();
  }

  function buildMoneyFormatter(sThousand, sFraction) {
    var s1 = _mfClean(sThousand), s2 = _mfClean(sFraction);
    var m1 = s1.match(/\d[\d.,'   ]*\d|\d/);
    if (!m1) return null;
    var core = m1[0];
    var decimals = 0, dsep = "";
    var m2 = (s2.match(/\d[\d.,'   ]*\d|\d/) || [])[0];
    if (m2) {
      var t = m2.match(/([.,])(\d{1,2})$/);
      if (t) { dsep = t[1]; decimals = t[2].length; }
    }
    var body = core;
    if (decimals) body = body.replace(new RegExp("\\" + dsep + "\\d{" + decimals + "}$"), "");
    var tm = body.match(/^\d+([^\d])\d{3}$/);
    return {
      prefix: s1.slice(0, m1.index),
      suffix: s1.slice(m1.index + core.length),
      tsep: tm ? tm[1] : "",
      dsep: dsep || ".",
      decimals: decimals
    };
  }

  var _MF = null, _MFkey = null;

  function mf() {
    var key = (SHOP.money_sample_thousand || "") + "|" + (SHOP.money_sample_fraction || "");
    if (_MFkey === key) return _MF;
    _MFkey = key;
    _MF = SHOP.money_sample_thousand
      ? buildMoneyFormatter(SHOP.money_sample_thousand, SHOP.money_sample_fraction)
      : null;
    return _MF;
  }

  function money(cents, format) {
    var f = format ? null : mf();
    if (f) {
      var n = Number(cents) || 0, neg = n < 0;
      var s = (Math.abs(n) / 100).toFixed(f.decimals).split(".");
      var whole = f.tsep ? s[0].replace(/(\d)(?=(\d\d\d)+$)/g, "$1" + f.tsep) : s[0];
      return (neg ? "-" : "") + f.prefix + (f.decimals ? whole + f.dsep + s[1] : whole) + f.suffix;
    }
    var fmt = format || (SHOP.money_format || "${{amount}}");
    var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!m) return fmt;
    var v;
    switch (m[1]) {
      case "amount_no_decimals":                        v = fx(cents, 0, ",", "."); break;
      case "amount_with_comma_separator":               v = fx(cents, 2, ".", ","); break;
      case "amount_no_decimals_with_comma_separator":   v = fx(cents, 0, ".", ","); break;
      case "amount_with_space_separator":               v = fx(cents, 2, " ", ","); break;
      case "amount_no_decimals_with_space_separator":   v = fx(cents, 0, " ", ""); break;
      case "amount_with_period_and_space_separator":    v = fx(cents, 2, " ", "."); break;
      default:                                          v = fx(cents, 2, ",", ".");
    }
    return fmt.replace(m[0], v);
  }

  /* ── Shopify image resizing ─────────────────────────────────────────────────
     A cart line's image is whatever size the theme happened to store. Asking the CDN
     for the size we actually paint keeps a drawer with 6 lines from pulling 6 MB. */
  function img(url, w) {
    if (!url) return "";
    if (url.indexOf("cdn.shopify.com") === -1 && url.indexOf("/cdn/") === -1) return url;
    return url.replace(/(\.(png|jpe?g|webp|gif))(\?.*)?$/i, "_" + w + "x$1$3");
  }

  /* ── State ─────────────────────────────────────────────────────────────────── */

  var SHOP = window.EightCartShop || {};
  var CFG = merge(DEFAULTS, window.EightCartConfig || {});
  var CART = { items: [], item_count: 0, total_price: 0, items_subtotal_price: 0,
               total_discount: 0, cart_level_discount_applications: [], note: "", currency: "" };
  var PREVIEW = !!window.EightCartPreview;
  var root = null, drawerEl = null, open = false, busy = false;
  var annTimer = null, annIdx = 0;

  /* ── Cart API ───────────────────────────────────────────────────────────────
     In preview mode every one of these is a no-op: the cockpit supplies the cart. */

  function ajax(url, opts) {
    opts = opts || {};
    return fetch(url, {
      method: opts.method || "GET",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      credentials: "same-origin"
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) throw (j && (j.description || j.message)) || ("HTTP " + r.status);
        return j;
      });
    });
  }

  /* Has a real cart ever been loaded? Until it has, the drawer must not open on the empty
     placeholder above — it would flash "your cart is empty" over a cart that has three
     things in it. After the first load it stays true, and every open is instant. */
  var cartKnown = false;

  /* ANSWERS ARRIVE OUT OF ORDER. A switch flipped off and on again fires change.js,
     update.js, add.js and update.js within a second, and Shopify does not answer them in
     the order they were sent. A late answer from before the add would put a cart WITHOUT
     the cover on screen, and the next reconcile would add it again — Shopify merges that
     into quantity 2, and the customer pays twice (seen on Modehaus, 2026-09-15). Every
     request takes a ticket when it is SENT; an answer only lands if its ticket is newer than
     the one already on screen. */
  var cartSeq = 0, cartApplied = 0;
  function ticket() { return ++cartSeq; }
  function adopt(c, seq) {
    if (!c || !c.items) return CART;
    if (seq < cartApplied) return CART;       // stale: a newer answer is already in
    cartApplied = seq;
    CART = c; cartKnown = true; syncThemeCount();
    return CART;
  }

  /* WAIT FOR A WHOLE CART, NOT HALF OF ONE.
     Straight after a change, Shopify will hand back a cart whose discount is already counted
     in the money but whose discount APPLICATION has no title yet. Painting on that produced
     the staged appearance the owner kept seeing: first a chip with only an icon in it, and
     once I refused to draw a nameless chip, the word "Discounts" arriving a beat before its
     badge. Both times I was moving the seam instead of removing it. One short retry gets the
     finished cart, and then there is only ever one thing to draw. */
  function fetchCart(isRetry) {
    if (PREVIEW) return Promise.resolve(CART);
    var seq = ticket();
    return ajax(routed("/cart.js")).then(function (c) {
      var half = (c.cart_level_discount_applications || []).some(function (d) {
        return !String(d.title || "").trim();
      });
      if (half && !isRetry) {
        return new Promise(function (go) { setTimeout(go, 180); }).then(function () {
          return fetchCart(true);
        });
      }
      adopt(c, seq);
      reconcileProtection();                 // not awaited: the drawer paints on this cart now
      return CART;
    });
  }

  // Markets/locale: a store on /de or /en-gb serves its cart under that prefix.
  function routed(path) {
    var pre = (SHOP.routes_root || "/").replace(/\/$/, "");
    return pre + path;
  }

  function changeLine(key, qty) {
    if (PREVIEW) {
      for (var i = 0; i < CART.items.length; i++) {
        if (CART.items[i].key === key) {
          if (qty <= 0) CART.items.splice(i, 1); else CART.items[i].quantity = qty;
          break;
        }
      }
      recount();
      return Promise.resolve(CART);
    }
    var seq = ticket();
    return ajax(routed("/cart/change.js"), { method: "POST", body: { id: key, quantity: qty } })
      .then(function (c) { return adopt(c, seq); });
  }

  // Recompute the derived totals the preview needs (the real cart gets these from Shopify).
  function recount() {
    var n = 0, sub = 0, disc = 0;
    for (var i = 0; i < CART.items.length; i++) {
      var it = CART.items[i];
      n += it.quantity;
      sub += (it.original_line_price !== undefined ? it.original_line_price : it.line_price);
      disc += ((it.original_line_price || it.line_price) - it.line_price);
    }
    CART.item_count = n;
    CART.items_subtotal_price = sub - disc;
    CART.original_total_price = sub;
    CART.total_discount = disc;
    CART.total_price = sub - disc;
  }

  /* ── Rewards maths ──────────────────────────────────────────────────────────
     The bar is INFORMATION. The money is Shopify's: a tier either maps to an
     automatic discount (which applies itself at checkout) or to a code we hand over.
     We never invent a price — that is the one thing a cart app must not do. */

  function tiers() {
    var t = (CFG.rewards.tiers || []).slice();
    t.sort(function (a, b) { return tierTarget(a) - tierTarget(b); });
    return t;
  }

  function T(key) {
    var v = (CFG.text || {})[key];
    return v === undefined || v === null || v === "" ? (DEFAULTS.text[key] || "") : v;
  }

  function rewardLabel(t) {
    if (!t) return "";
    if (t.text) return t.text;
    if (t.type === "free_shipping") return T("free_shipping");
    if (t.type === "percent") return T("percent_off").replace(/\{VALUE\}/g, t.value || 0);
    if (t.type === "fixed") return T("amount_off").replace(/\{AMOUNT\}/g, money(Math.round((t.value || 0) * 100)));
    if (t.type === "gift") return T("free_gift");
    return T("reward");
  }

  // What a tier is measured against: money in cents, or a plain item count.
  function tierTarget(t) {
    return CFG.rewards.basis === "itemCount"
      ? Math.max(0, parseInt(t.count, 10) || 0)
      : Math.round((parseFloat(t.amount) || 0) * 100);
  }

  /* ── Shipping protection ────────────────────────────────────────────────────
     The cover is a line in Shopify's cart and a switch in ours. Everything that counts,
     lists or judges "the items" has to look past that one line, or the header says three
     where the customer put two, and the reward bar counts the insurance as a garment. */
  function isProt(it) {
    var p = CFG.protection;
    if (!p || !p.enabled || !it) return false;
    if (it.properties && it.properties._ec_protection) return true;
    return !!p.product_id && String(it.product_id) === String(p.product_id);
  }

  function goods() {
    var out = [], items = CART.items || [];
    for (var i = 0; i < items.length; i++) if (!isProt(items[i])) out.push(items[i]);
    return out;
  }

  function protLine() {
    var items = CART.items || [];
    for (var i = 0; i < items.length; i++) if (isProt(items[i])) return items[i];
    return null;
  }

  // The preview has no Shopify to hold the line, so the switch remembers its own state there.
  var protPreviewOn = false;
  // The switch answers the tap BEFORE Shopify does. Measured on Modehaus Hartmann: the two
  // requests behind a toggle take 0.6-0.9 s, and a switch that only moves once the server has
  // answered reads as broken. `protOverride` holds the state the customer asked for until the
  // real cart confirms or refutes it.
  var protOverride = null;
  function protOn() {
    if (PREVIEW) return protPreviewOn;
    if (protOverride !== null) return protOverride;
    return !!protLine();
  }

  // What the tier is judged on: the goods, never the cover itself (a cover priced on a total
  // that includes the cover is a circle), and before or after discounts as configured.
  function protBasis() {
    var g = goods(), sum = 0, pre = CFG.protection.pre_discount;
    for (var i = 0; i < g.length; i++) {
      var it = g[i];
      sum += pre ? (it.original_line_price !== undefined ? it.original_line_price : it.line_price)
                 : (it.line_price !== undefined ? it.line_price : it.original_line_price);
    }
    return sum;
  }

  // Tiers, cheapest ceiling first, the open-ended one last. Shared by the variant lookup
  // and by the preview's own price so both walk the same ladder.
  function protLadder(list, priceKey, uptoKey) {
    var out = [];
    for (var i = 0; i < (list || []).length; i++) {
      var t = list[i], pr = parseFloat(t[priceKey]);
      if (!t || isNaN(pr) || pr <= 0) continue;
      var up = t[uptoKey];
      up = (up === null || up === undefined || up === "") ? null : parseFloat(up);
      if (up !== null && isNaN(up)) up = null;
      out.push({ price: pr, upto: up, id: t.id });
    }
    out.sort(function (a, b) {
      if (a.upto === null) return 1;
      if (b.upto === null) return -1;
      return a.upto - b.upto;
    });
    return out;
  }

  function protPick(ladder, basis) {
    if (!ladder.length) return null;
    for (var i = 0; i < ladder.length; i++) {
      if (ladder[i].upto === null || basis <= ladder[i].upto) return ladder[i];
    }
    return ladder[ladder.length - 1];         // above every ceiling: the top tier applies
  }

  // The Shopify variant that carries today's price for this cart. Null until the product
  // has been created: a switch that cannot add anything is not drawn.
  function protVariant() {
    var p = CFG.protection;
    var ladder = protLadder(p.variants, "price", "upto");   // both in cents
    if (!ladder.length) return null;
    if (p.pricing !== "tiers") return ladder[0];
    return protPick(ladder, protBasis());
  }

  // The price the CONFIG asks for, in cents — what the preview shows before the product
  // exists, and what the editor compares the shop against.
  function protConfigPrice() {
    var p = CFG.protection;
    if (p.pricing === "tiers") {
      var ladder = protLadder(p.tiers, "price", "upto"), hit;
      for (var i = 0; i < ladder.length; i++) {
        ladder[i].price = Math.round(ladder[i].price * 100);
        if (ladder[i].upto !== null) ladder[i].upto = Math.round(ladder[i].upto * 100);
      }
      hit = protPick(ladder, protBasis());
      return hit ? hit.price : 0;
    }
    return Math.round((parseFloat(p.price) || 0) * 100);
  }

  // The switch remembers a "no" for the visit, so a pre-selected cover does not climb back
  // into the cart on the next add after the customer took it out.
  function protDeclined(set) {
    try {
      if (set === true) window.sessionStorage.setItem("ec-prot-off", "1");
      else if (set === false) window.sessionStorage.removeItem("ec-prot-off");
      return window.sessionStorage.getItem("ec-prot-off") === "1";
    } catch (e) { return false; }
  }

  // The total the customer sees while a switch is in flight: Shopify's figure, plus the cover
  // that is being added, minus the cover that is being removed. So the tap and the button
  // agree at once, and the server's answer changes nothing visible when it lands.
  function shownTotal(base) {
    var t = base || 0;
    if (PREVIEW || protOverride === null) return t;
    var line = protLine(), v = protVariant();
    if (protOverride === true && !line && v) return t + v.price;
    if (protOverride === false && line) return t - (line.line_price || line.price || 0);
    return t;
  }

  function countedItems() {
    var n = Math.max(0, CART.item_count || 0);
    if (!CFG.protection.count_in_items) {
      var pl = protLine();
      if (pl) n -= (pl.quantity || 0);
    }
    return Math.max(0, n);
  }

  function cartProgress() {
    if (CFG.rewards.basis === "itemCount") return countedItems();
    return CFG.rewards.pre_discount
      ? (CART.original_total_price || CART.total_price || 0)
      : (CART.total_price || 0);
  }

  // Which tiers are met, and what is left to the next one — in cents or in items,
  // depending on the basis. `remaining` carries whichever unit is in play; the caller
  // formats it (money vs a bare number) rather than guessing here.
  /* How far along the bar the fill should sit. Each tier owns an equal SEGMENT, and the fill
     crosses a segment as the cart closes on that tier — so the bar and the milestone markers
     agree with each other. Measured against Upcart: 1 item towards a 2/3/4 ladder fills half
     of the first third of the bar. */
  function segmentPct(list, have) {
    if (!list.length) return 0;
    var seg = 100 / list.length, prev = 0;
    for (var i = 0; i < list.length; i++) {
      var target = tierTarget(list[i]);
      if (have < target) {
        var span = target - prev;
        return Math.max(0, Math.min(100, (i + (span > 0 ? (have - prev) / span : 0)) * seg));
      }
      prev = target;
    }
    return 100;
  }

  function rewardState() {
    var list = tiers(), have = cartProgress(), i;
    var reached = -1;
    for (i = 0; i < list.length; i++) {
      if (have >= tierTarget(list[i])) reached = i;
    }
    var next = list[reached + 1] || null;
    var top = list.length ? tierTarget(list[list.length - 1]) : 0;
    return {
      list: list,
      reached: reached,
      next: next,
      remaining: next ? Math.max(0, tierTarget(next) - have) : 0,
      // The bar fills ONE SEGMENT PER TIER, not as a single ratio of the final target.
      // With tiers at 2/3/4 items and one item in the cart, Upcart fills half of the first
      // segment (16.6% of the bar); a plain have/top gives 25% and the marker sits visibly
      // past where Upcart puts it.
      pct: segmentPct(list, have)
    };
  }

  /* ── Render ─────────────────────────────────────────────────────────────────
     Pure: config + cart in, HTML string out. The cockpit calls exactly this. */

  function cssVars(d) {
    var h = CFG.header, a = CFG.announcements;
    // Measured off the live Upcart on Grant & Morgan: its "base" drawer is ~500px wide.
    // At the 400 I originally picked, ours read cramped beside it.
    // 440px is Upcart's actual "base" width, read off the live drawer on Grant & Morgan.
    var w = { narrow: "380px", default: "440px", wide: "520px" }[d.width_desktop] || "440px";
    return [
      "--ec-bg:" + d.bg,
      "--ec-accent:" + d.accent,
      "--ec-text:" + d.text,
      "--ec-savings:" + d.savings,
      "--ec-btn:" + d.btn_color,
      "--ec-btn-text:" + d.btn_text,
      "--ec-btn-radius:" + (d.btn_radius | 0) + "px",
      "--ec-input-radius:" + (d.input_radius | 0) + "px",
      "--ec-img-radius:" + (d.img_radius | 0) + "px",
      "--ec-width:" + w,
      "--ec-bar:" + CFG.rewards.bar_color,
      "--ec-track:" + CFG.rewards.track_color,
      "--ec-btn-text-hover:" + (d.btn_text_hover || d.btn_text),
      "--ec-subtotal:" + (d.subtotal_color || d.text),
      "--ec-line:" + (d.line_color || "rgba(0,0,0,.07)"),
      "--ec-chip-bg:" + (d.chip_bg || "rgba(0,0,0,.05)"),
      "--ec-chip-text:" + (d.chip_text || "#585858"),
      "--ec-input-border:" + (d.input_border || "rgba(0,0,0,.2)"),
      "--ec-head-pad:" + (h.height === "slim" ? "16px" : "30px"),
      // 30, not 31: the header's bottom border counts towards Upcart's 97px, and ours is
      // border-box, so the border has to come out of the padding rather than sit on top.
      "--ec-head-pad-b:" + (h.height === "slim" ? "14px" : "30px"),
      "--ec-head-border:" + ({ none: "0", thin: "1px", thick: "2px" }[h.border_bottom] || "1px"),
      "--ec-head-bg:" + (h.bg || d.bg),
      "--ec-logo-size:" + (parseInt(h.logo_size, 10) || 34) + "px",
      "--ec-x-bg:" + (h.close_bg || d.accent),
      "--ec-x-bg-hover:" + (h.close_bg_hover || h.close_bg || d.accent),
      "--ec-x-color:" + (h.close_icon_color || d.text),
      "--ec-x-color-hover:" + (h.close_icon_hover || h.close_icon_color || d.text),
      "--ec-x-icon:" + ({ small: "10px", medium: "15px", large: "18px" }[h.close_icon_size] || "15px"),
      "--ec-x-stroke:" + (h.close_icon_thickness === "thick" ? "2.4" : "1.6"),
      "--ec-x-border:" + ({ none: "0", thin: "1px", normal: "2px", thick: "3px" }[h.close_border] || "0"),
      "--ec-x-border-color:" + h.close_border_color,
      // 4px, not 4.5: the bar now carries a border top AND bottom, and those two pixels
      // come out of the padding rather than being added to Upcart's 26px height.
      "--ec-ann-pad:" + ({ slim: "4px", normal: "8px", thick: "13px" }[a.height] || "8px"),
      "--ec-ann-fs:" + (parseFloat(a.font_size) || 13) + "px",
      "--ec-ann-border:" + (a.border_color ? "1px solid " + a.border_color : "0"),
      "--ec-sticky-size:" + (parseInt(CFG.sticky.size, 10) || 54) + "px",
      "--ec-sticky-bg:" + (CFG.sticky.bg || d.btn_color),
      "--ec-sticky-icon:" + (CFG.sticky.icon_color || d.btn_text),
      "--ec-sticky-badge-bg:" + (CFG.sticky.badge_bg || d.accent),
      "--ec-sticky-badge-text:" + (CFG.sticky.badge_text || d.text),
      // A REACHED milestone is a circle FILLED with the bar colour, so its icon has to be the
      // colour that reads ON the bar — the button text, i.e. white on almost every store.
      // Falling back to the bar colour drew a black tag on a black circle: LUMA, live at the
      // time, showed three solid blobs where the rewards were supposed to be celebrated. The
      // same mistake as the white-on-white one a day earlier, from the other end.
      "--ec-node-done:" + (CFG.rewards.icon_done || d.btn_text),
      // An UNREACHED one sits on the track, so it takes the bar colour like its ring.
      "--ec-node-todo:" + (CFG.rewards.icon_todo || CFG.rewards.bar_color)
    ].join(";");
  }

  var ICONS = {
    tag: '<path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 2.8 12V4a1.2 1.2 0 0 1 1.2-1.2h8a2 2 0 0 1 1.4.6l7.2 7.2a2 2 0 0 1 0 2.8Z"/><circle cx="7.2" cy="7.2" r="1.2"/>',
    truck: '<path d="M2.5 6.5h10v9h-10zM12.5 9.5h4l3 3v3h-7z"/><circle cx="6" cy="17" r="1.6"/><circle cx="16" cy="17" r="1.6"/>',
    gift: '<path d="M3 8.5h18v3H3zM4.5 11.5h15v9h-15zM12 8.5v12"/><path d="M12 8.5C12 6 10.5 4 8.8 4S6 5.2 6 6.4 7.4 8.5 12 8.5Zm0 0C12 6 13.5 4 15.2 4S18 5.2 18 6.4 16.6 8.5 12 8.5Z"/>',
    // The sticky button's own icon set. A shop that sells jewellery does not want a
    // supermarket trolley in the corner of every page.
    bag_bag: '<path d="M4 6.5h15l-1.4 8.4a2 2 0 0 1-2 1.6H7.6a2 2 0 0 1-2-1.7L4 4.2H1.8"/><circle cx="9" cy="20" r="1.4"/><circle cx="17" cy="20" r="1.4"/>',
    bag_cart: '<path d="M6 7.5h12l-1 11.5a1.8 1.8 0 0 1-1.8 1.6H8.8A1.8 1.8 0 0 1 7 19zM9 9.5V6a3 3 0 0 1 6 0v3.5"/>',
    bag_basket: '<path d="M3.5 9.5h17l-1.6 9.2a2 2 0 0 1-2 1.6H7.1a2 2 0 0 1-2-1.6zM8.5 3.8 6.4 9.5m9.1-5.7 2.1 5.7M9.6 13v4m4.8-4v4"/>',
    star: '<path d="m12 3.5 2.6 5.4 5.9.8-4.3 4.1 1.1 5.9-5.3-2.9-5.3 2.9 1.1-5.9L3.5 9.7l5.9-.8Z"/>',
    // The shipping-protection row's default mark.
    shield: '<path d="M12 3 4.5 6v5.2c0 4.6 3.2 8.4 7.5 9.8 4.3-1.4 7.5-5.2 7.5-9.8V6L12 3Z"/><path d="m9 12 2 2 4-4.5"/>'
  };

  /* The glyph inside a discount chip is not our outlined tag. Upcart uses Shopify's own
     Polaris "discount tag" — a SOLID mark at 16px, not a 1.6px outline at 11px. Two icons
     of the same thing in two different drawing styles is the sort of difference you feel
     before you can name it. Kept separate from svg() so the milestone circles, which ARE
     outlined in Upcart, do not get dragged along with it. */
  var CHIP_TAG = '<path fill-rule="evenodd" d="M8.575 4.649a3.75 3.75 0 0 1 2.7-1.149h1.975a3.25 ' +
    '3.25 0 0 1 3.25 3.25v2.187a3.25 3.25 0 0 1-.996 2.34l-4.747 4.572a2.5 2.5 0 0 1-3.502-.033l' +
    '-2.898-2.898a2.75 2.75 0 0 1-.036-3.852l4.254-4.417Zm4.425 3.351a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"/>';

  /* The milestone tag, drawn exactly as Upcart draws it: Shopify's Polaris tag as a FILLED
     shape with an even-odd hole, not an outline. Mine was a hand-drawn 24x24 stroked at
     1.6px — same object, different hand, and next to theirs it reads as a different icon at
     a glance without being nameable. Second time this exact mistake: the discount chip was
     the first. Anything of theirs that looks outlined is worth checking for a fill. */
  var NODE_TAG =
    '<path d="M13 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"/>' +
    '<path fill-rule="evenodd" d="M11.276 3.5a3.75 3.75 0 0 0-2.701 1.149l-4.254 4.417a2.75 ' +
    '2.75 0 0 0 .036 3.852l2.898 2.898a2.5 2.5 0 0 0 3.502.033l4.747-4.571a3.25 3.25 0 0 0 ' +
    '.996-2.341v-2.187a3.25 3.25 0 0 0-3.25-3.25h-1.974Zm-1.62 2.19a2.25 2.25 0 0 1 ' +
    '1.62-.69h1.974c.966 0 1.75.784 1.75 1.75v2.187c0 .475-.194.93-.536 1.26l-4.747 4.572a1 ' +
    '1 0 0 1-1.401-.014l-2.898-2.898a1.25 1.25 0 0 1-.016-1.75l4.253-4.418Z"/>';

  function nodeIcon(cls) {
    return '<svg class="' + (cls || "") + '" viewBox="0 0 20 20" fill="currentColor" ' +
           'aria-hidden="true">' + NODE_TAG + "</svg>";
  }

  /* v1's discount chip carries the same ring-shaped tag as the milestones (NODE_TAG), not v2's
     solid one; read off Harper & Wells' live drawer, 2026-09-24. */
  function chipIcon(cls, v1) {
    return '<svg class="' + (cls || "") + '" viewBox="0 0 20 20" fill="currentColor" ' +
           'aria-hidden="true">' + (v1 ? NODE_TAG : CHIP_TAG) + "</svg>";
  }

  function svg(name, cls) {
    return '<svg class="' + (cls || "") + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
           'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
           (ICONS[name] || ICONS.tag) + "</svg>";
  }

  function renderHeader(c) {
    var h = c.header, count = countedItems();
    var title = h.title
      ? esc(h.title) + (h.show_count ? ' <span class="ec-count">(' + count + ")</span>" : "")
      : (h.logo ? '<img class="ec-logo" src="' + safeImg(h.logo) + '" alt="' + esc(SHOP.name || "") + '" />'
                : '<span class="ec-wordmark">' + esc(SHOP.name || "Cart") + "</span>");
    return '<header class="ec-head ec-head--' + esc(h.align) +
             " ec-close--" + esc(h.close_side) + (h.sticky ? " is-sticky" : "") + '">' +
             '<div class="ec-head-title">' + title + "</div>" +
             '<button type="button" class="ec-x" data-ec-close aria-label="' + esc(T("aria_close")) + '">' +
               '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
               'stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>' +
             "</button>" +
           "</header>";
  }

  function renderAnnouncements(c) {
    var a = c.announcements;
    if (!a.enabled || !a.items || !a.items.length) return "";
    var out = "";
    for (var i = 0; i < a.items.length; i++) {
      var it = a.items[i] || {};
      var body = esc(it.text || "")
        .replace(/\{TIMER\}/g, '<span class="ec-ann-timer" data-ec-timer>--:--</span>');
      if (it.link) body = '<a href="' + safeUrl(it.link) + '">' + body + "</a>";
      out += '<div class="ec-ann-slide' + (i === 0 ? " is-on" : "") + '">' + body + "</div>";
    }
    return '<div class="ec-ann" style="background:' + esc(a.bg) + ";color:" + esc(a.color) + '">' + out + "</div>";
  }

  /* The reward's name, ready to drop into a sentence. Escaped here — the caller must not
     escape it again, or the link would print as markup. When the tier names a product and
     the setting is on, it becomes a link: "a free gift" tells the customer nothing about
     what they are working towards. */
  function rewardName(t) {
    var name = esc(rewardLabel(t));
    if (!t || !CFG.rewards.product_link) return name;
    var url = t.url || (t.handle ? "/products/" + t.handle : "");
    return url ? '<a class="ec-reward-link" href="' + safeUrl(url) + '">' + name + "</a>" : name;
  }

  function renderRewards(c) {
    var r = c.rewards;
    if (!r.enabled || !r.tiers || !r.tiers.length) return "";
    if (!r.show_on_empty && !goods().length) return "";
    var st = rewardState(), i, nodes = "";
    // One tier means one icon sitting at the far end of a bar that never moves past it.
    // Upcart hides it unless you ask for it, because it reads as a bar that failed to fill.
    var drawNodes = st.list.length > 1 || r.single_icon;
    for (i = 0; drawNodes && i < st.list.length; i++) {
      var done = i <= st.reached;
      nodes += '<span class="ec-node' + (done ? " is-done" : "") + '" style="left:' +
               (st.list.length === 1 ? 100 : ((i + 1) / st.list.length) * 100) + '%" ' +
               'title="' + esc(rewardLabel(st.list[i])) + '">' +
               (r.icon === "tag" ? nodeIcon("ec-node-ic") : svg(r.icon, "ec-node-ic")) + "</span>";
    }
    var byCount = r.basis === "itemCount";
    var msg;
    if (!st.next) {
      msg = (st.list[st.reached] && st.list[st.reached].after) || r.achieved_text || "";
      msg = esc(msg).replace(/\{\{\s*reward\s*\}\}/g, rewardName(st.list[st.reached]));
      if (!msg) msg = esc(T("all_unlocked"));
    } else {
      // Per-tier wording first — Upcart writes a different line for each tier ("an EXTRA
      // 10% / 15% / 20% discount") and one global sentence cannot say all three.
      var tpl = st.next.before || (byCount ? r.progress_text_count : r.progress_text) || "";
      var left = byCount ? String(st.remaining) : money(st.remaining);
      var b0 = r.bold_count === false ? "" : "<b>", b1 = r.bold_count === false ? "" : "</b>";
      msg = esc(tpl)
        .replace(/\{AMOUNT\}|\{\{\s*remaining\s*\}\}/g, b0 + esc(left) + b1)
        .replace(/\{COUNT\}/g, b0 + esc(String(st.remaining)) + b1)
        // {PLURAL:one:many} — Upcart writes "You're 2 product away", which is wrong in
        // English and worse in Spanish ("Te falta 2 producto"). One extra token lets the
        // sentence agree with the number without writing two whole messages.
        .replace(/\{PLURAL:([^:}]*):([^}]*)\}/g, function (_m, one, many) {
          return esc(st.remaining === 1 ? one : many);
        })
        .replace(/\{\{\s*reward\s*\}\}/g, "<b>" + rewardName(st.next) + "</b>");
    }
    return '<div class="ec-rewards">' +
             '<p class="ec-rewards-msg">' + msg + "</p>" +
             '<div class="ec-track"><span class="ec-fill" style="width:' + st.pct.toFixed(1) + '%"></span>' + nodes + "</div>" +
           "</div>";
  }

  /* handle -> { variant id: compare_at_price }. Filled in the background; a line simply
     shows no saving until its product has landed, rather than holding the drawer shut. */
  var compareCache = {};

  function compareAt(it) {
    // A line that already carries its own compare-at wins. Shopify's /cart.js never sends
    // one, so live this is always the fetched cache — but the cockpit preview builds its
    // lines from the Admin API, where the price IS on the variant, and loadCompareAt is
    // skipped in preview. Without this the preview could not show a strikethrough at all,
    // whatever the shop had set, and I read that absence back as "these products have no
    // compare-at price" — arguing from the bug to prove the bug.
    if (it.compare_at_price) return Number(it.compare_at_price) || 0;
    // PRINTED BY THE SHOP, SO THE FIRST PAINT ALREADY HAS IT. The fetch below is a second
    // paint, and a second paint is a visible one: the owner watched the drawer open on the
    // bare sale price and the struck price and the saving appear a beat later. Liquid knows
    // the compare-at of every line in the cart and of the product being viewed, so it hands
    // them over at page render — the same trick Upcart uses with upcartPreloadedCart, which
    // is why Upcart never blinks. Anything not in here still arrives the slow way.
    var pre = SHOP.compare_at && SHOP.compare_at[String(it.variant_id)];
    if (pre) return Number(pre) || 0;
    var m = compareCache[it.handle || ""];
    return (m && Number(m[String(it.variant_id)])) || 0;
  }

  function loadCompareAt() {
    if (!CFG.design.strikethrough || PREVIEW) return Promise.resolve();
    var want = [], seen = {}, items = CART.items || [];
    for (var i = 0; i < items.length; i++) {
      var h = items[i].handle;
      if (h && !seen[h] && !(h in compareCache)) { seen[h] = 1; want.push(h); }
    }
    if (!want.length) return Promise.resolve();
    return Promise.all(want.map(function (h) {
      return fetch("/products/" + encodeURIComponent(h) + ".js", { credentials: "same-origin" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          var m = {};
          if (p && p.variants) {
            for (var j = 0; j < p.variants.length; j++) {
              m[String(p.variants[j].id)] = p.variants[j].compare_at_price;
            }
          }
          compareCache[h] = m;
        })
        // A product that 404s (deleted, or a gift card) must not take the drawer with it.
        .catch(function () { compareCache[h] = {}; });
    }));
  }

  function lineDiscountBadges(it) {
    var da = it.line_level_discount_allocations || it.discounts || [];
    var out = "";
    for (var i = 0; i < da.length; i++) {
      var t = (da[i].discount_application && da[i].discount_application.title) || da[i].title || "";
      if (t) out += '<span class="ec-badge">' + chipIcon("ec-badge-ic") + esc(t) + "</span>";
    }
    return out;
  }

  function renderItem(it, c) {
    var d = c.design;
    // Two different things can make a line "was X, now Y", and only one of them is in
    // /cart.js. A cart discount shows up as original_line_price. A compare-at price — which
    // is how EVERY one of these stores actually prices ("€149,95, now €44,95") — is not in
    // the cart payload at all, so it has to be fetched from the product. Without this the
    // drawer showed a bare €44,95 while Upcart, right next to it, showed the €105 saving.
    // THE COMPARE-AT PRICE WINS, ALWAYS. Upcart shows "$449.85 → $179.85 (Save $270.00)"
    // on three shirts even while a 15% cart discount is running — the struck price is the
    // shop's own before-price, not the cart's running total. I had let the cart discount
    // take over whenever Shopify allocated it to the line, so the same three shirts read
    // "$179.85 → $152.88 (Save $26.97)": a tenth of the saving, and it flickered between
    // the two depending on whether the discount had landed yet when the drawer painted.
    var ca = compareAt(it);
    var orig = ca > it.price
      ? ca * (it.quantity || 1)
      : (it.original_line_price !== undefined ? it.original_line_price : it.line_price);
    // The live price is the line BEFORE cart discounts. Shopify's line_price already has
    // them taken off, so using it made three shirts read "$449.85 → $152.88 (Save $296.97)"
    // — a saving that double-counts the very discount the footer states separately.
    var now = it.original_line_price !== undefined ? it.original_line_price : it.line_price;
    var cut = d.strikethrough && orig > now;
    var v1 = d.card === "v1";
    var v2n = d.card === "v2n";
    var drawn = v1 || v2n;           // Upcart's drawn stepper marks and typed quantity
    var opts = "";
    if (it.options_with_values) {
      var lines = [];
      for (var i = 0; i < it.options_with_values.length; i++) {
        var o = it.options_with_values[i];
        if (o && o.value && String(o.value).toLowerCase() !== "default title") {
          // v1 USED to write "Color :" (measured 2026-08-14); Upcart dropped that space since,
          // and Harper & Wells' live drawer reads "Color: Black" (re-measured 2026-09-24).
          lines.push('<span class="ec-opt-k">' + esc(o.name) + ": " + "</span>" +
                     '<span class="ec-opt-v">' + esc(o.value) + "</span>");
        }
      }
      if (v1) {
        for (var j = 0; j < lines.length; j++) opts += '<div class="ec-opt">' + lines[j] + "</div>";
      } else if (lines.length) {
        opts = '<div class="ec-opt">' + lines.join('<span class="ec-opt-sep">, </span>') + "</div>";
      }
    }
    var props = "";
    if (it.properties) {
      for (var k in it.properties) {
        if (!Object.prototype.hasOwnProperty.call(it.properties, k)) continue;
        if (!it.properties[k]) continue;
        // _foo is app bookkeeping (bundle ids, subscription tokens), not something a
        // customer should ever read off their own cart line.
        if (k.charAt(0) === "_") continue;
        props += '<div class="ec-opt ec-opt--prop">' + esc(k) + ": " + esc(it.properties[k]) + "</div>";
      }
    }
    var price =
      '<div class="ec-price">' +
        (cut ? '<s class="ec-was">' + esc(money(orig)) + "</s> " : "") +
        '<span class="ec-now">' + esc(money(now)) + "</span>" +
        (cut ? ' <span class="ec-save">' +
               esc(T("save").replace(/\{AMOUNT\}/g, money(orig - now))) + "</span>" : "") +
      "</div>";

    return '<div class="ec-item" data-ec-key="' + esc(it.key) + '">' +
             '<a class="ec-thumb ec-thumb--' + esc(d.img_ratio) + '" href="' + safeUrl(it.url) + '">' +
               // NOT lazy: the drawer only exists because the customer asked to see their
               // cart, and there are a handful of lines at most. Lazy here defers exactly
               // the thing they opened it for — and inside a scroll container the browser
               // may never decide to load it at all.
               (it.image ? '<img src="' + safeImg(img(it.image, 200)) + '" alt="' + esc(it.product_title || "") + '" />' : "") +
             "</a>" +
             '<div class="ec-item-main">' +
               '<div class="ec-item-top">' +
                 '<a class="ec-item-title" href="' + safeUrl(it.url) + '">' + esc(it.product_title || it.title) + "</a>" +
                 '<button type="button" class="ec-remove" data-ec-remove aria-label="' + esc(T("aria_remove")) + '">' +
                    (v1 ? V1_TRASH : v2n ? V2N_TRASH :
                    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M4 7h16M9 7V5h6v2M6.5 7l.8 12.2A1.5 1.5 0 0 0 8.8 20.6h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7"/></svg>') +
                  "</button>" +
               "</div>" +
               opts + props +
               // WHERE THE MONEY GOES. v2 puts the price on its own line ABOVE the quantity
               // box, left aligned. v1 puts it to the RIGHT of the quantity box, right
               // aligned, with the saving wrapping onto a second line. Same numbers, opposite
               // corner of the row — copying one onto the other is the single most visible
               // way to get "close but not it".
               (v1 ? "" : price) +
               '<div class="ec-item-bottom">' +
                 '<div class="ec-qty ec-qty--box">' +
                   '<button type="button" data-ec-qty="-1" aria-label="' + esc(T("aria_less")) + '">' + (drawn ? V1_MINUS : "&minus;") + '</button>' +
                   // v1's number is a field the customer can type in (Upcart: type=number, 0-9999,
                   // applied on leaving the field or Enter, 0 removes, empty puts it back).
                   (drawn ? '<input class="ec-qty-n ec-qty-in" data-ec-qty-in type="number" inputmode="numeric" min="0" max="9999" maxlength="4" aria-label="' + esc(T("aria_qty")) + '" value="' + (it.quantity | 0) + '" />'
                       : '<span class="ec-qty-n">' + (it.quantity | 0) + "</span>") +
                   '<button type="button" data-ec-qty="1" aria-label="' + esc(T("aria_more")) + '">' + (drawn ? V1_PLUS : "+") + '</button>' +
                 "</div>" +
                 (v1 ? price : "") +
               "</div>" +
               (lineDiscountBadges(it) ? '<div class="ec-badges">' + lineDiscountBadges(it) + "</div>" : "") +
             "</div>" +
           "</div>";
  }

  /* Upcart v1's own glyphs, copied off Harper & Wells' live drawer (2026-09-24): a grey
     #8F8F8F outline bin and 12px stepper marks. Drawn, not typed, because the text "−" and "+"
     sit at a different weight and height than the icons the shoppers of that store know. */
  var V1_TRASH = '<svg viewBox="0 0 15 15" fill="none"><path d="M3.28125 3.28125L3.86719 12.6562C3.89502 13.1979 4.28906 13.5938 4.80469 13.5938H10.1953C10.713 13.5938 11.0997 13.1979 11.1328 12.6562L11.7188 3.28125" stroke="#8F8F8F" stroke-width="0.9375" stroke-linecap="round" stroke-linejoin="round"/><path d="M2.34375 3.28125H12.6562H2.34375Z" fill="#8F8F8F"/><path d="M2.34375 3.28125H12.6562" stroke="#8F8F8F" stroke-width="0.9375" stroke-miterlimit="10" stroke-linecap="round"/><path d="M9.60938 5.15625L9.375 11.7188M5.625 3.28125V2.10938C5.62473 2.01697 5.64273 1.92541 5.67797 1.83998C5.71321 1.75455 5.76499 1.67693 5.83034 1.61159C5.89568 1.54624 5.9733 1.49446 6.05873 1.45922C6.14416 1.42398 6.23571 1.40598 6.32812 1.40625H8.67188C8.76429 1.40598 8.85584 1.42398 8.94127 1.45922C9.0267 1.49446 9.10432 1.54624 9.16966 1.61159C9.23501 1.67693 9.28679 1.75455 9.32203 1.83998C9.35727 1.92541 9.37527 2.01697 9.375 2.10938V3.28125H5.625ZM7.5 5.15625V11.7188V5.15625ZM5.39062 5.15625L5.625 11.7188L5.39062 5.15625Z" stroke="#8F8F8F" stroke-width="0.9375" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  /* Upcart's newer bin (Polaris "delete", filled #637381 at 20px on a 24px button), off
     Wilson & Carter's live drawer, 2026-09-25. */
  var V2N_TRASH = '<svg viewBox="0 0 20 20" fill="#637381"><path d="M11.5 8.25a.75.75 0 0 1 .75.75v4.25a.75.75 0 0 1-1.5 0v-4.25a.75.75 0 0 1 .75-.75Z"/><path d="M9.25 9a.75.75 0 0 0-1.5 0v4.25a.75.75 0 0 0 1.5 0v-4.25Z"/><path fill-rule="evenodd" d="M7.25 5.25a2.75 2.75 0 0 1 5.5 0h3a.75.75 0 0 1 0 1.5h-.75v5.45c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311c-.642.327-1.482.327-3.162.327h-.4c-1.68 0-2.52 0-3.162-.327a3 3 0 0 1-1.311-1.311c-.327-.642-.327-1.482-.327-3.162v-5.45h-.75a.75.75 0 0 1 0-1.5h3Zm1.5 0a1.25 1.25 0 1 1 2.5 0h-2.5Zm-2.25 1.5h7v5.45c0 .865-.001 1.423-.036 1.848-.033.408-.09.559-.128.633a1.5 1.5 0 0 1-.655.655c-.074.038-.225.095-.633.128-.425.035-.983.036-1.848.036h-.4c-.865 0-1.423-.001-1.848-.036-.408-.033-.559-.09-.633-.128a1.5 1.5 0 0 1-.656-.655c-.037-.074-.094-.225-.127-.633-.035-.425-.036-.983-.036-1.848v-5.45Z"/></svg>';
  var V1_MINUS = '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5 10c0-.414.336-.75.75-.75h8.5c.414 0 .75.336.75.75s-.336.75-.75.75h-8.5c-.414 0-.75-.336-.75-.75Z"/></svg>';
  var V1_PLUS = '<svg viewBox="0 0 20 20" fill="currentColor"><path d="M10.75 5.75c0-.414-.336-.75-.75-.75s-.75.336-.75.75v3.5h-3.5c-.414 0-.75.336-.75.75s.336.75.75.75h3.5v3.5c0 .414.336.75.75.75s.75-.336.75-.75v-3.5h3.5c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-3.5v-3.5Z"/></svg>';

  function renderItems(c) {
    var list = goods();
    if (!list.length) {
      return '<div class="ec-empty">' + slot("empty") +
               '<p class="ec-empty-txt">' + esc(c.items.empty_text) + "</p>" +
               (c.items.empty_cta ? '<a class="ec-empty-cta" href="' + safeUrl(c.items.empty_cta_link) + '">' +
                  esc(c.items.empty_cta) + "</a>" : "") +
             "</div>";
    }
    var out = "", between = slot("between_items");
    for (var i = 0; i < list.length; i++) {
      if (i) out += between;
      out += renderItem(list[i], c);
    }
    return '<div class="ec-items">' + out + "</div>";
  }

  /* Whether the last code was refused, and what the customer typed. Held as STATE rather
     than poked into the DOM: the first version appended the message to the form, and the
     next repaint — the compare-at prices arriving a moment later — wiped it out. The
     customer saw a code fail with no explanation at all. Anything that must survive a
     repaint has to be rendered from state, not attached to the markup afterwards. */
  var codeError = false;
  var codeTyped = "";

  function renderCodes(c) {
    if (!c.codes.enabled) return "";
    return '<form class="ec-codes" data-ec-code-form>' +
             '<input class="ec-code-input" type="text" placeholder="' + esc(c.codes.placeholder) + '" aria-label="' + esc(c.codes.label) + '" value="' + esc(codeTyped) + '" />' +
             '<button type="submit" class="ec-code-btn">' + esc(c.codes.button) + "</button>" +
             (codeError ? '<p class="ec-code-err">' + esc(T("code_invalid")) + "</p>" : "") +
           "</form>";
  }

  /* The last discount name Shopify gave us, held for as long as a discount is running. See
     renderDiscounts: the amount arrives a second before the word that explains it. */
  var lastDiscountName = "";

  function renderDiscounts(c) {
    var apps = CART.cart_level_discount_applications || [];
    var total = CART.total_discount || 0;

    // THE ROW IS ALWAYS THERE, even with nothing in it. Returning "" when there is no
    // discount meant the row did not exist one paint and did exist the next, which is a
    // change of SHAPE — morph refuses those and hands the caller a rebuilt subtree, so the
    // badge appeared out of nowhere and vanished the same way. That is the popping the owner
    // kept seeing, and it survived the in-place update because there was nothing in place to
    // update. Rendered empty and hidden, the node lives across every paint, its text is
    // swapped where it stands, and CSS fades it. One badge slot, not a variable number, for
    // the same reason: a changing child count is a changing shape.
    var on = !!(apps.length || total);
    var name = "";
    for (var i = 0; i < apps.length && !name; i++) {
      // NO CHIP WITHOUT A NAME. Shopify hands the discount back before its title is
      // populated often enough to see it: the first paint drew a chip containing only the
      // tag icon, and the name arrived in the next one. From the customer's side the
      // little badge grew in two steps, which is the "first the icon, then the text" the
      // owner spotted. A chip with nothing written on it says nothing worth saying — it
      // waits until it can say the whole thing.
      name = String(apps[i].title || "").trim();
    }
    // AND WHILE IT WAITS, IT KEEPS THE WORD IT HAD.
    //
    // Waiting for the name is right on the first paint and wrong on every one after it.
    // Measured on Grace & Oliver, sampling the chip every 40ms while going from two items to
    // three: the new amount was on screen at once and the new title took a full second, so
    // for that second the chip had nothing to say and hid itself. From the customer's side
    // the badge vanishes and comes back — which is exactly what the owner reported, and what
    // Upcart does not do.
    //
    // So a chip that is already up stays up on the last name it was given, and swaps the
    // text in place when the new one lands. The trade is one second of "10% EXTRA DISCOUNT"
    // beside an amount that already says 15%, against a badge that blinks out on every
    // quantity change. The blink is the thing you see; the word is the thing you read after.
    // A discount scoped to a COLLECTION (which every all-products discount becomes once the
    // shipping cover is kept out of it, see cart.scope_discounts) is no longer cart-level:
    // Shopify allocates it per line and the cart-level list is empty. The money still shows
    // in total_discount; the NAME has to be read off the lines. Measured on LUMA 2026-09-15:
    // "Descuentos −$9,998" with an empty chip, where "10% EXTRA DE DESCUENTO" had stood.
    if (!name && on) {
      var its = CART.items || [];
      for (var j = 0; j < its.length && !name; j++) {
        var la = its[j].line_level_discount_allocations || [];
        for (var k = 0; k < la.length && !name; k++) {
          name = String((la[k].discount_application && la[k].discount_application.title) || "").trim();
        }
      }
    }
    if (!name && on) name = lastDiscountName;
    lastDiscountName = on ? name : "";
    return '<div class="ec-row ec-row--disc' + (on ? " is-on" : "") + '">' +
             '<span class="ec-row-l">' + esc(c.discounts.label) +
               ' <span class="ec-badge' + (name ? " is-on" : "") + '">' +
                 chipIcon("ec-badge-ic", c.design.card === "v1") + '<span class="ec-badge-t">' + esc(name) + '</span>' +
               '</span>' +
             "</span>" +
             // v1 prints a plain hyphen ("- $15.99"), not a minus sign (Harper & Wells, 2026-09-24).
             '<span class="ec-row-r">' + (c.design.card === "v1" || c.design.card === "v2n" ? "- " : "&minus; ") + esc(money(total)) + "</span>" +
           "</div>";
  }

  /* Where the checkout button points. The cart permalink is what every checkout replacement
     on the market recognises — Shopify's own checkout follows it just as happily, so this is
     the right URL whether a store runs Lasso or not. Falls back to /checkout only if Shopify
     did not hand us a token. */
  function checkoutUrl() {
    return CART.token ? routed("/cart/c/" + CART.token) : routed("/checkout");
  }

  function renderSummary(c) {
    var s = c.summary, out = "";
    // One switch, not two: Design owns whether the subtotal line exists. It had a second
    // checkbox in Cart summary saying the same thing, which is a setting you can turn on and
    // watch nothing happen.
    if (c.design.subtotal_line) {
      out += '<div class="ec-row"><span class="ec-row-l">' + esc(T("subtotal")) + "</span>" +
             '<span class="ec-row-r">' + esc(money(shownTotal(CART.items_subtotal_price !== undefined ? CART.items_subtotal_price : CART.total_price))) + "</span></div>";
    }
    out += renderDiscounts(c);
    var label = esc(s.checkout_text) + (s.show_total_on_button ? " &bull; " + esc(money(shownTotal(CART.total_price))) : "");
    var empty = !goods().length;
    // A REAL LINK TO THE CART PERMALINK, not a button that jumps to /checkout.
    //
    // Five of these stores check out through Lasso, not through Shopify. Lasso replaces the
    // checkout by watching for the customer leaving for one — and what it watches is a link.
    // Our button navigated with JavaScript, so Lasso never saw it and every one of those
    // orders would have gone to Shopify's own checkout instead: the whole point of Lasso,
    // silently bypassed, on a cart that otherwise looked perfect. The owner caught it in the
    // preview; I had clicked the same button myself and written it down as working.
    //
    // Upcart's is <a href="/cart/c/{cart.token}">. Shopify's cart token already carries the
    // signing key, so this is the entire fix. Verified: Upcart's link lands on
    // checkout.watson-and-carter.com; ours landed on watson-and-carter.com/checkouts/cn/.
    out += empty
      ? '<button type="button" class="ec-checkout" data-ec-checkout disabled>' + label + "</button>"
      : '<a class="ec-checkout" data-ec-checkout href="' + safeUrl(checkoutUrl()) + '">' + label + "</a>";
    if (c.design.continue_shopping) {
      out += '<button type="button" class="ec-continue" data-ec-close>' +
             esc(c.design.continue_text) + "</button>";
    }
    return '<div class="ec-summary">' + out + "</div>";
  }

  // The default mark: a black tile with a fine white line shield and a tick — the inverse of
  // the product thumbnails beside it, and the same black as the checkout button under it.
  // Owner, 2026-09-15, after a blue 3D badge and a solid black glyph: "premium, wees een
  // echte designer". Thin lines on black read as engraved; solid shapes read as an app icon.
  var PROT_MARK =
    '<svg class="ec-prot-mark" viewBox="0 0 64 64" aria-hidden="true">' +
      '<rect width="64" height="64" rx="10" fill="#111"/>' +
      '<path d="M32 13.25 17.75 18.1V29.75c0 9.5 6 16.6 14.25 21 8.25-4.4 14.25-11.5 14.25-21V18.1L32 13.25Z" fill="none" stroke="#fff" stroke-width="1.65" stroke-linejoin="round"/>' +
      '<path d="m25.25 32.75 4.9 4.9 9-9.75" fill="none" stroke="#fff" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  /* The row: icon, title, description, price, switch. Drawn only when there is something
     to protect and something to sell — no goods, no row; no product in Shopify yet, no row
     on the live shop (the preview still shows it, priced off the config, so the owner can
     design it before the product exists). */
  function renderProtection(c) {
    var p = c.protection;
    if (!p.enabled) return "";
    if (!goods().length) return "";
    var v = protVariant();
    if (!v && !PREVIEW) return "";
    var cents = v ? v.price : protConfigPrice();
    var on = protOn();
    var sw = p.switch_color ? ' style="--ec-sw:' + esc(p.switch_color) + '"' : "";
    // Upcart's layout, read off their recording: a 64px picture on a light tile at the left,
    // the title with the PRICE at the end of the same line, the description in grey under it,
    // and the switch alone at the far right, centred on the row. No card, no border: the row
    // sits straight on the footer.
    return '<div class="ec-prot' + (on ? " is-on" : "") + '" data-ec-prot>' +
             '<div class="ec-prot-ic">' +
               (p.image ? '<img src="' + safeImg(p.image) + '" alt="" />' : PROT_MARK) +
             "</div>" +
             '<div class="ec-prot-main">' +
               '<div class="ec-prot-head">' +
                 '<span class="ec-prot-title">' + esc(p.title) + "</span>" +
                 '<span class="ec-prot-price">' + esc(money(cents)) + "</span>" +
               "</div>" +
               (p.description ? '<div class="ec-prot-desc">' + esc(p.description) + "</div>" : "") +
             "</div>" +
             '<button type="button" class="ec-switch" role="switch" aria-checked="' + (on ? "true" : "false") +
               '" data-ec-prot-toggle aria-label="' + esc(p.title) + '"' + sw + ">" +
               '<svg class="ec-switch-tick" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>' +
               '<span class="ec-switch-knob"></span>' +
             "</button>" +
           "</div>";
  }

  function renderTrust(c) {
    if (!c.trust.enabled) return "";
    if (!c.trust.image && !c.trust.text) return "";
    return '<div class="ec-trust">' +
             (c.trust.text ? '<p class="ec-trust-txt">' + esc(c.trust.text) + "</p>" : "") +
             // NOT loading="lazy". The badge is inserted while the drawer is still parked
             // off-screen (is-visible before is-open, so the slide has a start frame), and a
             // lazy image that far outside the viewport is one the browser is entitled never
             // to fetch — measured on Grace & Oliver, where it stayed 384×0 through a full
             // page life and left a blank strip under the checkout button. Marking it eager
             // costs one small request on a drawer the customer has already opened, and on
             // G&O the badge is the ONLY thing Upcart draws that we have to draw too.
             (c.trust.image ? '<img class="ec-trust-img" src="' + safeImg(c.trust.image) +
                              '" alt="" />' : "") +
           "</div>";
  }

  /* The whole drawer, as one string. This is the function the cockpit preview calls. */
  function render(cfg, cart) {
    if (cfg) CFG = merge(DEFAULTS, cfg);
    if (cart) { CART = cart; if (PREVIEW) recount(); }
    var c = CFG, a = c.announcements;

    var body =
      slot("below_header") +
      (a.enabled && a.position === "top" ? renderAnnouncements(c) : "") +
      renderRewards(c) +
      renderItems(c) +
      (a.enabled && a.position === "bottom" ? renderAnnouncements(c) : "") +
      slot("bottom");

    return '<div class="ec-backdrop" data-ec-backdrop></div>' +
           '<aside class="ec-drawer ec-card--' + esc(c.design.card === "v2n" ? "v2 ec-card--v2n" : c.design.card) + " ec-side--" + esc(c.design.side) +
               " ec-mobile--" + esc(c.design.width_mobile) +
             // is-empty lets a card style the empty drawer as a whole: Upcart v1 shows nothing
             // but the header and one centred line when there is nothing in it.
             (goods().length ? "" : " is-empty") +
             (c.design.inherit_fonts ? "" : " ec-own-font") + '" role="dialog" aria-modal="true" aria-label="' + esc(T("aria_cart")) + '">' +
             slot("above_header") +
             renderHeader(c) +
             '<div class="ec-body">' + body + "</div>" +
             '<footer class="ec-foot">' +
               slot("above_footer") +
               renderCodes(c) + slot("above_checkout") + renderProtection(c) + renderSummary(c) +
               slot("below_checkout") + renderTrust(c) +
             "</footer>" +
           "</aside>";
  }

  /* ── Mount + behaviour ─────────────────────────────────────────────────────── */

  /* ── The shadow root ────────────────────────────────────────────────────────
     The drawer lives inside a shadow tree, which is the single reason Upcart looks
     identical on nine different themes and mine did not. A storefront theme is free to
     write `div { line-height: 2 }` or `button { text-transform: uppercase }`, and every
     one of those rules used to land inside our cart. Nothing crosses a shadow boundary,
     so the drawer now renders the same everywhere by construction rather than by me
     chasing each theme's quirks.

     `root` still points at the inner .ec-root element, so every render, query and event
     handler below is unchanged — only its address moved. */
  var hostEl = null, shadow = null;

  function ensureRoot() {
    if (root && hostEl && document.body.contains(hostEl)) return root;
    hostEl = document.createElement("div");
    hostEl.setAttribute("data-eight-cart", "1");
    // DAWN HIDES EMPTY DIVS. Its base.css carries `div:empty { display: none }`, and a host
    // whose only content is a shadow tree IS empty to CSS. Measured on Smith Titanium
    // (Dawn 15.4, 2026-09-15): the drawer reported open, every rect read 0×0, the customer
    // saw nothing. An inline !important display is the one thing no theme stylesheet can
    // outrank, and it costs nothing on the themes that never had the rule.
    hostEl.style.setProperty("display", "block", "important");
    document.body.appendChild(hostEl);
    shadow = hostEl.attachShadow ? hostEl.attachShadow({ mode: "open" }) : null;
    var mount = shadow || hostEl;                 // no shadow support: fall back, still works
    if (shadow) {
      // The stylesheet has to live INSIDE the shadow tree; a <link> in the page head
      // cannot reach across the boundary. The browser has it cached from the head
      // preload, so this costs nothing on the second open.
      var css = (SHOP && SHOP.css_url) || "";
      if (css) {
        var link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = css;
        mount.appendChild(link);
      }
    }
    root = document.createElement("div");
    root.className = "ec-root";
    mount.appendChild(root);
    return root;
  }

  /* True from the moment the slide starts until it has finished. Rebuilding the drawer's
     entire markup mid-slide is the one thing guaranteed to drop frames: the browser has to
     re-lay-out and re-decode every product image while it is trying to animate a transform.
     The compare-at fetch lands somewhere inside that window on a normal connection, so this
     was firing on very nearly every open. */
  var animating = false;
  var pendingPaint = false;
  // The markup currently on screen. Lets a repaint that would change nothing do nothing —
  // see paint(). Cleared whenever something else writes into the root behind our back.
  var lastHtml = null;
  var _animT = null;

  // ── Updating without rebuilding ─────────────────────────────────────────────
  //
  // MEASURED on the live shop, 2026-08-14: adding one item replaced the whole `.ec-drawer`
  // FOUR times. paintThen deliberately draws more than once — immediately from the cart we
  // already know, again when the fresh one lands, again after the compare-at prices — and each
  // of those was `innerHTML = render()`, which destroys every node and builds new ones.
  //
  // That single fact caused all three of the owner's complaints at once. A node that has just
  // been born has no previous value, so a CSS transition has nothing to interpolate: the
  // reward bar jumped to its new length instead of travelling there, and the discount chip
  // popped in and out instead of changing. Four full rebuilds in a row is also the white
  // flicker he saw. The start-frame trick below was me treating the symptom on the one element
  // I had noticed; the cause was that we were throwing the drawer away.
  //
  // So: when the new markup has the same SHAPE as what is on screen, walk the two trees and
  // copy across only what actually differs — text, attributes, styles. Elements survive, so
  // transitions work by themselves, exactly as they do for Upcart (which never rebuilds).
  // When the shape genuinely changed — an item removed, a section switched on — that subtree
  // is replaced, and only that subtree.
  function morph(cur, next) {
    if (cur.nodeType === 3) {                       // text: the cheapest and most common case
      if (cur.nodeValue !== next.nodeValue) cur.nodeValue = next.nodeValue;
      return true;
    }
    if (cur.nodeType !== 1 || next.nodeType !== 1) return false;
    if (cur.tagName !== next.tagName) return false;
    // The class list is our structural fingerprint: `.ec-fill` staying `.ec-fill` means the
    // same bar, while `is-on` appearing is a state change we want to apply, not rebuild for.
    var a = cur.getAttribute("class") || "", b = next.getAttribute("class") || "";
    if (a.split(" ")[0] !== b.split(" ")[0]) return false;
    if (cur.childNodes.length !== next.childNodes.length) return false;

    for (var i = 0; i < next.attributes.length; i++) {
      var at = next.attributes[i];
      if (cur.getAttribute(at.name) !== at.value) cur.setAttribute(at.name, at.value);
    }
    for (var j = cur.attributes.length - 1; j >= 0; j--) {
      var nm = cur.attributes[j].name;
      if (!next.hasAttribute(nm)) cur.removeAttribute(nm);
    }
    // A field the script or the customer has written to no longer follows its value
    // ATTRIBUTE, so the server's quantity would never reach it. Push it, unless the customer
    // is typing in it right now.
    if (cur.tagName === "INPUT" && next.hasAttribute("value") &&
        !(cur.getRootNode && cur.getRootNode().activeElement === cur) &&
        cur.value !== next.getAttribute("value")) cur.value = next.getAttribute("value");
    for (var k = 0; k < next.childNodes.length; k++) {
      var c = cur.childNodes[k], n = next.childNodes[k];
      if (!morph(c, n)) cur.replaceChild(n.cloneNode(true), c);
    }
    return true;
  }

  function paint() {
    if (animating) { pendingPaint = true; return; }
    var r = ensureRoot();
    var prev = r.querySelector(".ec-body");
    var scroll = prev ? prev.scrollTop : 0;
    var oldFill = r.querySelector(".ec-fill");
    var fillFrom = oldFill ? oldFill.style.width : null;
    r.setAttribute("style", cssVars(CFG.design));

    var html = render();
    // Three of the four paints after an add produce byte-identical markup once the data has
    // settled. Doing nothing at all is the best possible update: no flicker, no interrupted
    // transition, no work.
    if (html === lastHtml && r.firstChild) return;
    lastHtml = html;

    if (r.firstChild) {
      var staging = document.createElement("div");
      staging.innerHTML = html;
      var same = staging.childNodes.length === r.childNodes.length;
      if (same) {
        for (var m = 0; m < staging.childNodes.length && same; m++) {
          same = morph(r.childNodes[m], staging.childNodes[m]);
        }
      }
      if (!same) r.innerHTML = html;
    } else {
      r.innerHTML = html;
    }
    drawerEl = r.querySelector(".ec-drawer");
    var body = r.querySelector(".ec-body");
    if (body && scroll) body.scrollTop = scroll;
    applyCustomCss();
    startAnnouncements();
    startCountdown();

    // THE REWARD BAR HAS TO ANIMATE, and a freshly created element never transitions — it is
    // simply born at its final width. We render the new bar, put it back to the OLD width for
    // one frame, and then release it, so the browser has two values to interpolate between.
    // Upcart keeps its element alive and gets this for free; we rebuild the markup, so we hand
    // the browser the start frame ourselves. Same trick as the drawer's own slide, one level
    // down — and the reason the bar was jumping straight to its new length.
    var fill = r.querySelector(".ec-fill");
    if (fill && fillFrom !== null && fillFrom !== fill.style.width) {
      var to = fill.style.width;
      fill.style.transition = "none";
      fill.style.width = fillFrom;
      void fill.offsetWidth;
      fill.style.transition = "";
      // rAF for the smooth case, a timer as the guarantee: requestAnimationFrame does not
      // fire in a background tab, and without the second call the bar would sit at its old
      // width until something else redrew it. A stale progress bar is worse than an
      // un-animated one — it tells the customer they are further from the reward than they
      // are. The isConnected check keeps a later repaint from being undone by an earlier
      // callback writing to a node that has already been replaced.
      var settle = function () { if (fill.isConnected) fill.style.width = to; };
      requestAnimationFrame(settle);
      setTimeout(settle, 60);
    }
  }

  var cdTimer = null;

  function applyCustomCss() {
    // The sticky button is mounted in the same shadow tree as the drawer, so one style tag
    // serves both. They are two settings because they are two different things to think
    // about: the drawer you open, and the button that is on every page all the time.
    var c = CFG.custom || {};
    var css = (c.css || "") + (c.css_sticky ? "\n" + c.css_sticky : "");
    var mount = shadow || root;
    if (!mount) return;
    var tag = mount.querySelector ? mount.querySelector("style[data-ec-custom]") : null;
    if (!css) { if (tag) tag.remove(); return; }
    if (!tag) {
      tag = document.createElement("style");
      tag.setAttribute("data-ec-custom", "1");
      mount.appendChild(tag);          // last in the tree, so it beats the stylesheet
    }
    tag.textContent = css;
  }

  function startCountdown() {
    if (cdTimer) { clearInterval(cdTimer); cdTimer = null; }
    var secs = parseInt(CFG.announcements.countdown, 10) || 0;
    var els = root.querySelectorAll("[data-ec-timer]");
    if (!secs || !els.length) return;
    var left = secs;
    function tick() {
      var m = Math.floor(left / 60), ss = left % 60;
      var txt = (m < 10 ? "0" : "") + m + ":" + (ss < 10 ? "0" : "") + ss;
      for (var i = 0; i < els.length; i++) els[i].textContent = txt;
      if (left <= 0) { clearInterval(cdTimer); cdTimer = null; return; }
      left--;
    }
    tick();
    cdTimer = setInterval(tick, 1000);
  }

  function startAnnouncements() {
    if (annTimer) { clearInterval(annTimer); annTimer = null; }
    var slides = root.querySelectorAll(".ec-ann-slide");
    if (slides.length < 2) return;
    annIdx = 0;
    annTimer = setInterval(function () {
      slides[annIdx].classList.remove("is-on");
      annIdx = (annIdx + 1) % slides.length;
      slides[annIdx].classList.add("is-on");
    }, Math.max(1500, CFG.announcements.rotate_ms | 0));
  }

  var _closeT = null, _sweepT = null, _padded = false;

  // Locking the page is a full-document reflow. Done at the same instant the slide starts it
  // lands in frame 1 and the animation drops it. Done here — before the forced layout read in
  // setOpen — it is absorbed into that single synchronous block instead.
  //
  // ⚠️ INLINE, NOT A CLASS (2026-09-24). This used to add `html.ec-locked` and rely on the rule
  // in eight-cart.css, but the page never APPLIES that file: both installs only preload it and
  // link it inside the shadow root, where a rule about <html> reaches nothing. So on every store
  // the page kept scrolling behind the open cart and slid 15px sideways from the padding. It is
  // now written onto <body> directly, which is exactly what Upcart does (overflow hidden, no
  // padding: measured on Harper & Wells' live Upcart drawer).
  var _bodyOv = null;
  function lockScroll(on) {
    var d = document.documentElement, b = document.body;
    if (on) {
      if (_bodyOv === null) { _bodyOv = b.style.overflow || ""; b.style.overflow = "hidden"; }
      d.classList.add("ec-locked");
    } else {
      d.classList.remove("ec-locked");
      if (_bodyOv !== null) { b.style.overflow = _bodyOv; _bodyOv = null; }
      if (_padded) { b.style.paddingRight = ""; _padded = false; }
    }
  }

  function setOpen(v) {
    open = v;
    var r = ensureRoot();
    if (v) {
      clearTimeout(_closeT);
      // TWO steps, and the order is the whole trick. The drawer markup is built and shown
      // in the same breath as the open class, so the browser never renders the closed
      // state — with no start frame there is nothing to animate from and the panel simply
      // appears. Making it visible, forcing a layout read, and only THEN sliding gives the
      // browser its start frame. This is why it popped instead of gliding.
      lockScroll(true);                         // before the read, so its reflow is absorbed
      r.classList.add("is-visible");
      void r.offsetWidth;                       // forces layout — do not remove
      r.classList.add("is-open");
      animating = true;
      clearTimeout(_animT);
      _animT = setTimeout(function () {
        animating = false;
        if (pendingPaint) { pendingPaint = false; paint(); }
      }, 300);                                  // the slide is 249ms; a little air after it
    } else {
      r.classList.remove("is-open");
      // Stay mounted until the slide-out has actually played, or closing pops too.
      _closeT = setTimeout(function () {
        if (!open) r.classList.remove("is-visible");
      }, 360);
    }
    if (!v) lockScroll(false);
    document.body.classList.toggle("ec-open", v);
    if (v && drawerEl) {
      var x = drawerEl.querySelector("[data-ec-close]");
      if (x) x.focus();
    }
    if (v) {
      // AFTER the slide, never during. A theme opens its own drawer a beat later anyway, so
      // waiting costs nothing and buys back every frame of the animation.
      clearTimeout(_sweepT);
      _sweepT = setTimeout(function () { sweepThemeDrawers(); setTimeout(sweepThemeDrawers, 500); }, 460);
    } else {
      clearTimeout(_sweepT);
      restoreThemeDrawers();
    }
  }

  function openDrawer() {
    if (!CFG.enabled && !PREVIEW) return;
    paintThen(function () { setOpen(true); });
  }

  /* The compare-at fetch must never be able to hold the drawer shut. It gets a short
     head start; whatever has not landed by then arrives in a second paint a moment later,
     which is a price quietly gaining its strikethrough rather than a cart that hangs. */
  /* No race any more: nothing waits on this. The drawer is already open by the time the
     product prices land, and the repaint is held until the slide has played out. */
  function compareAtSoon() {
    return loadCompareAt().then(function () { if (open) paint(); });
  }

  /* OPEN FIRST, FILL IN AFTER.
     This used to wait for the compare-at prices before the drawer was allowed to move — up
     to half a second of nothing happening after the customer clicked "add to cart", on a
     slide that itself lasts a quarter of one. Upcart opens the instant you click. So do we
     now: the cart itself is all the drawer needs to paint, and everything slower arrives in
     a repaint that the animation guard holds until the slide has finished. */
  // Set by the add interceptors: the drawer is opening because something was just added.
  // Opening instantly on the cached cart then showed the OLD cart first, the new item a
  // beat later, and the cover a beat after that — three jumps in two seconds (measured on
  // Modehaus). After an add the drawer now waits for the fresh cart and, when the cover is
  // pre-selected, for the cover too, and slides in once with everything on it.
  var pendingAdd = false;

  function paintThen(cb) {
    var go = function () {
      paint();
      if (cb) cb();
    };

    // ALREADY KNOW WHAT IS IN THE CART? THEN MOVE NOW.
    // Waiting on /cart.js put a whole network round trip — a second on a phone — between the
    // tap and the first pixel of movement. No animation curve survives that; it reads as the
    // cart hanging, which is exactly what it was. Upcart prints the cart into the page so it
    // never has to ask. We keep the last one we were given, open on it immediately, and let
    // the fresh copy arrive in a repaint the slide guard holds until the drawer has landed.
    var afterAdd = pendingAdd; pendingAdd = false;
    var instant = cartKnown && !PREVIEW && !afterAdd;
    if (instant) go();

    fetchCart().then(function () { if (!instant) go(); },
      function (e) {
        if (!instant) go();                   // a cart that will not load still needs a way through
        if (window.console) console.warn("[Eight Cart]", e);
      }).then(function () {
      if (instant && open) paint();           // the fresh copy, once the slide is done
    }).then(function () {
      return compareAtSoon();
    }).then(function () {
      if (open) paint();                      // held until the slide is done
    }).catch(function (e) {
      if (window.console) console.warn("[Eight Cart]", e);
    });
  }

  function refresh() {
    if (!open) return fetchCart().then(updateStickyCount);
    return fetchCart()
      .then(function () { paint(); updateStickyCount(); return loadCompareAt(); })
      .then(function () { if (open) paint(); });
  }

  /* THE THEME'S OWN CART INDICATOR, WHICH NOBODY WAS UPDATING.
     The little bubble beside the bag in the header belongs to the theme, and the theme keeps
     it right by running its own cart javascript. We take that javascript's job away — that is
     the whole point of the drawer — and then never did the one thing it was still doing. So
     emptying the cart left the bubble sitting there and filling an empty cart lit nothing up,
     until the page was reloaded. Exactly what the owner described, and Upcart does not have it
     because refreshing those elements is part of what you buy.
     Two shapes exist on this estate, both read off the live shops rather than guessed:
       · Impulse and its relatives (all four live stores): an EMPTY span whose visibility is a
         modifier class, `cart-link__bubble--visible`. There is no number to write.
       · Van Leeuwen's theme: `<span class="counter cart-counter hidden">0</span>`, a real
         number that hides itself with `hidden` at zero.
     Anything else keeps whatever it had: a node is only written to when what it holds today is
     a number, so a bag icon or a word is never overwritten with a digit. */
  function syncThemeCount() {
    // NOT BEFORE WE KNOW THE CART. `countedItems()` reads zero until the first /cart.js lands,
    // and writing that zero out would blank a bubble the shop had rendered correctly, for the
    // couple of hundred milliseconds until the answer arrives. A flicker introduced by the fix
    // for a flicker is not a fix.
    if (PREVIEW || !cartKnown) return;
    var n = countedItems(), i, els, el;

    els = document.querySelectorAll(".cart-link__bubble");
    for (i = 0; i < els.length; i++) {
      els[i].classList[n > 0 ? "add" : "remove"]("cart-link__bubble--visible");
    }

    els = document.querySelectorAll(
      ".cart-counter, .cart-count, .cart-item-count, .cart-count-bubble, [data-cart-count], #CartCount");
    for (i = 0; i < els.length; i++) {
      el = els[i];
      if (el.closest && el.closest(".ec-root")) continue;
      var target = /^\s*\d*\s*$/.test(el.textContent || "") ? el : null;
      if (!target) {                       // themes that wrap the figure in a span of its own
        var kids = el.querySelectorAll("span");
        for (var k = 0; k < kids.length && !target; k++) {
          if (/^\s*\d+\s*$/.test(kids[k].textContent || "")) target = kids[k];
        }
      }
      if (target) target.textContent = n;
      if (el.hasAttribute("data-cart-count")) el.setAttribute("data-cart-count", n);
      el.classList[n > 0 ? "remove" : "add"]("hidden");
    }
  }

  function updateStickyCount() {
    syncThemeCount();
    var b = (shadow || document).querySelector(".ec-sticky");
    if (!b) return;
    var n = countedItems();
    var badge = b.querySelector(".ec-sticky-n");
    if (badge) badge.style.display = CFG.sticky.show_count ? "" : "none";
    if (badge) badge.textContent = n;
    b.classList.toggle("is-hidden", CFG.sticky.hide_when_empty && !n);
  }

  function mountSticky() {
    ensureRoot();
    if (!CFG.sticky.enabled || (shadow || document).querySelector(".ec-sticky")) return;
    var b = el('<button type="button" class="ec-sticky ec-sticky--' + esc(CFG.sticky.position) +
                 " ec-sticky-on--" + esc(CFG.sticky.devices) + '" aria-label="' + esc(T("aria_open")) + '">' +
                 svg("bag_" + (CFG.sticky.icon_style || "bag"), "ec-sticky-ic") +
                 '<span class="ec-sticky-n">0</span></button>');
    b.addEventListener("click", openDrawer);
    (shadow || document.body).appendChild(b);
    updateStickyCount();
  }

  /* ── Events inside the drawer ──────────────────────────────────────────────── */

  function lineKey(node) {
    var row = node.closest ? node.closest("[data-ec-key]") : null;
    return row ? row.getAttribute("data-ec-key") : null;
  }

  function guard(fn) {
    return function () {
      // Tells the caller the click went nowhere, so an optimistic display can be left alone.
      if (busy) return false;
      busy = true;
      ensureRoot().classList.add("is-busy");
      var done = function () { busy = false; ensureRoot().classList.remove("is-busy"); };
      var p = fn.apply(null, arguments);
      if (p && p.then) p.then(done, done); else done();
      return true;
    };
  }

  /* THE DISCOUNT'S NAME IS THE ONE THING /cart/change.js WILL NOT TELL YOU.
     Measured on Grace & Oliver going from two items to three: the change response carries the
     new amount (1299) and a discount application whose `title` is literally null, while a
     /cart.js a moment later carries the same amount WITH "10% EXTRA DISCOUNT". So the row's
     figure and the checkout button were right immediately and the little chip beside them was
     a step behind, for as long as it took something else to refetch the cart — which is the
     lag the owner could see. One extra request, and only when a name is actually missing, is
     what closes it. A cart without discounts never pays for this. */
  function namesMissing() {
    var apps = (CART && CART.cart_level_discount_applications) || [];
    for (var i = 0; i < apps.length; i++) {
      if (!String(apps[i].title || "").trim()) return true;
    }
    return false;
  }

  function afterChange() {
    // ONE REPAINT, WITH THE WHOLE TRUTH — which is what Upcart does, measured rather than
    // assumed. Watching Upcart's own drawer through a MutationObserver while clicking plus:
    // the quantity figure moves at 13ms and then NOTHING moves until 1.0s to 1.6s, when the
    // discount line and the checkout total change together in a single mutation. It never
    // shows a new amount beside an old word.
    //
    // Painting on the change response and again on the follow-up was faster on paper (the
    // amount landed at 610ms against Upcart's 1.1s) and worse to watch: for 400ms the row
    // read "15% EXTRA DISCOUNT — £29.22" with the old word beside the new figure, and the
    // owner saw exactly that. The quantity box has already moved on its own, so nothing is
    // frozen while we wait; only the money holds still until it is all correct at once.
    //
    // A cart with no discount pending pays nothing for this: there is no second request and
    // the repaint happens immediately, as before.
    if (!PREVIEW && namesMissing()) {
      var done = function () { paint(); updateStickyCount(); };
      return fetchCart().then(done, done);
    }
    paint();
    updateStickyCount();
  }

  var onQty = guard(function (key, delta) {
    var it = null;
    for (var i = 0; i < CART.items.length; i++) if (CART.items[i].key === key) it = CART.items[i];
    if (!it) return null;
    return changeLine(key, Math.max(0, it.quantity + delta)).then(afterChange);
  });

  var onSet = guard(function (key, qty) {
    return changeLine(key, qty).then(afterChange);
  });

  var onRemove = guard(function (key) {
    return changeLine(key, 0).then(afterChange);
  });

  // The cover goes in through /cart/update.js (see addProtection). `ec-self` keeps our
  // own request out of the interceptor below, which would otherwise treat it as the theme
  // adding something and open the drawer a second time. The property is a second way to
  // recognise the line, for a shop whose product id was re-created underneath a live cart.
  // Upcart's "Include addon tracking on orders": a cart attribute that lands on the order as
  // "Shipping protection: yes", so the order itself says the cover was bought without anyone
  // reading the line items. Written on add, cleared on removal; a failure here never costs
  // the add itself (the attribute is bookkeeping, the line is the money).
  var PROT_ATTR = "Shipping protection";
  // /cart/update.js answers with the WHOLE cart, so writing the attribute doubles as the
  // re-read: no separate /cart.js afterwards. Two requests for an add, not three.
  function protAttribute(on) {
    if (PREVIEW) return Promise.resolve(CART);
    var attrs = {}; attrs[PROT_ATTR] = on ? "yes" : "";
    var seq = ticket();
    return ajax(routed("/cart/update.js?ec-self=1"), { method: "POST", body: { attributes: attrs } })
      .then(function (c) { return adopt(c, seq); }, function () { return CART; });
  }

  function addProtection() {
    var v = protVariant();
    if (!v) return Promise.resolve(CART);
    if (protLine()) return Promise.resolve(CART);   // already there: never a second one
    cartApplied = ticket();                          // anything answered before now is history
    // Through /cart/update.js, NOT /cart/add.js (2026-09-22). Klaviyo's "Added to Cart" (and
    // with it the abandoned-cart mail) listens to /cart/add.js, so a cover added there became
    // the customer's "last added product": Modehaus mailed "Ihr Warenkorb: Versandschutz".
    // update.js sets the variant to exactly 1 (never 2) and writes the order attribute in the
    // same call, and its answer is the whole cart. Recognised by product id, as before.
    var attrs = {}; attrs[PROT_ATTR] = "yes";
    var updates = {}; updates[v.id] = 1;
    var seq = ticket();
    return ajax(routed("/cart/update.js?ec-self=1"), {
      method: "POST",
      body: { updates: updates, attributes: attrs }
    }).then(function (c) { return adopt(c, seq); });
  }

  /* THE HARD FLOOR: exactly one cover, quantity one. Whatever races, whatever another
     script adds, a cart is never allowed to carry the cover twice — Shopify merges a double
     add into quantity 2 and the drawer, which never draws the line, would not show it. */
  function extraCover() {
    var items = CART.items || [], seen = null;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (!isProt(it)) continue;
      if (seen) return { key: it.key, qty: 0 };              // a second cover line: drop it
      seen = it;
      if ((it.quantity || 0) > 1) return { key: it.key, qty: 1 };
    }
    return null;
  }

  // The line goes first (that response is already the full cart, so the drawer can paint),
  // the attribute follows in the background: nobody waits on bookkeeping.
  function removeProtection(line) {
    return changeLine(line.key, 0).then(function (c) {
      protAttribute(false).then(function () { if (open) paint(); });
      return c;
    });
  }

  // A tap while the previous one is still in flight is not dropped: the switch moves at
  // once and the wish is kept; when the request settles it is applied if the cart still
  // disagrees. Tapping off-on-off quickly ends where the last tap left it, no more.
  var protQueued = null;
  function toggleProt() {
    if (busy) { protQueued = !protOn(); protOverride = protQueued; paint(); return; }
    onProtToggle();
  }
  var onProtToggle = guard(function () {
    var on = protOn();
    if (PREVIEW) { protPreviewOn = !on; paint(); return null; }
    protOverride = !on;                      // move the switch now
    paint();
    var settle = function () {
      protOverride = null;
      afterChange();
      setTimeout(function () {               // after the guard has let go
        if (protQueued === null) return;
        var want = protQueued; protQueued = null;
        if (want !== !!protLine()) toggleProt();
      }, 0);
    };
    if (on) {
      protDeclined(true);
      return removeProtection(protLine()).then(settle, settle);
    }
    protDeclined(false);
    return addProtection().then(settle, settle);
  });

  /* THE COVER FOLLOWS THE CART. Runs after every fresh /cart.js and does one of three
     things, or nothing at all:
       · the cover is the only thing left      → take it out (a cart of pure insurance is empty);
       · the goods moved into another tier     → swap the variant so the price matches again;
       · pre-selected, goods present, no cover → add it, unless the customer said no this visit.
     `reconciling` stops the fetch inside from calling straight back in here; a step that fails
     simply leaves the switch showing the truth, and the customer can press it. */
  var reconciling = false, reconcilePromise = null;
  function reconcileProtection() {
    if (PREVIEW || !CFG.protection.enabled) return Promise.resolve();
    if (reconciling) return reconcilePromise || Promise.resolve();
    var p = CFG.protection, line = protLine(), g = goods(), v = protVariant(), step = null;
    var extra = extraCover();
    if (extra) {
      step = function () { return changeLine(extra.key, extra.qty); };
    } else if (line && !g.length) {
      step = function () { return removeProtection(line); };
    } else if (line && v && String(line.variant_id) !== String(v.id)) {
      step = function () { return changeLine(line.key, 0).then(addProtection); };
    } else if (!line && g.length && v && p.preselect && !protDeclined() && !busy) {
      step = addProtection;                  // never while the customer's own tap is in flight
      protOverride = true;                   // drawn on, priced in, before Shopify answers
    }
    if (!step) return Promise.resolve();
    reconciling = true;
    // Every step above leaves CART holding Shopify's latest answer, so there is nothing
    // to re-read: one paint with the whole truth, as soon as the last response lands.
    var done = function () { reconciling = false; reconcilePromise = null; protOverride = null; if (open) paint(); updateStickyCount(); };
    reconcilePromise = step().then(done, function (e) {
      reconciling = false; reconcilePromise = null; protOverride = null;
      if (open) paint();
      if (window.console) console.warn("[Eight Cart] protection", e);
    });
    return reconcilePromise;
  }

  function bindRoot() {
    var r = ensureRoot();

    // The typed quantity (v1). "change" fires once the customer leaves the field or presses
    // Enter, which is exactly when Upcart applies it: never mid-typing.
    r.addEventListener("change", function (e) {
      var inp = e.target && e.target.closest && e.target.closest("[data-ec-qty-in]");
      if (!inp) return;
      var key = lineKey(inp), it = null;
      for (var i = 0; i < CART.items.length; i++) if (CART.items[i].key === key) it = CART.items[i];
      if (!it) return;
      var raw = String(inp.value).trim(), n = parseInt(raw, 10);
      if (raw === "" || isNaN(n) || n < 0) { inp.value = it.quantity; return; }
      n = Math.min(n, 9999);
      if (n === it.quantity) { inp.value = n; return; }
      if (!onSet(key, n)) inp.value = it.quantity;          // busy: the field says the truth
    });
    r.addEventListener("keydown", function (e) {
      var inp = e.target && e.target.closest && e.target.closest("[data-ec-qty-in]");
      if (inp && e.key === "Enter") { e.preventDefault(); inp.blur(); }
    });

    r.addEventListener("click", function (e) {
      var t = e.target;
      if (t.closest("[data-ec-close]")) { e.preventDefault(); return setOpen(false); }
      if (t.closest("[data-ec-backdrop]")) { if (CFG.behaviour.close_on_backdrop) setOpen(false); return; }

      var q = t.closest("[data-ec-qty]");
      if (q) {
        e.preventDefault();
        // Move the number NOW, before Shopify has been asked. A quantity step is the one
        // action a customer performs repeatedly, and waiting a round trip to see their own
        // tap register is what reads as lag — the request itself is not slow, the silence is.
        // The repaint that follows carries the real figure; if the server disagrees, its
        // number is the one that survives.
        // Ask FIRST, then move the number — and only if the request was actually accepted.
        // Bumping it before checking meant a tap that the busy lock dropped still moved the
        // figure, and the next repaint yanked it back: the quantity appearing to "trip" over
        // itself on fast taps. The display may now only ever run ahead of a request that is
        // genuinely on its way.
        var step = parseInt(q.getAttribute("data-ec-qty"), 10);
        var sent = onQty(lineKey(q), step);
        if (sent) {
          var box = q.parentNode.querySelector(".ec-qty-n");
          if (box) {
            var isIn = box.tagName === "INPUT";
            var next = (parseInt(isIn ? box.value : box.textContent, 10) || 0) + step;
            if (next > 0) { if (isIn) box.value = next; else box.textContent = next; }
          }
        }
        return;
      }

      var rm = t.closest("[data-ec-remove]");
      if (rm) { e.preventDefault(); return onRemove(lineKey(rm)); }

      var pt = t.closest("[data-ec-prot-toggle]");
      if (pt) { e.preventDefault(); return toggleProt(); }

      var co = t.closest("[data-ec-checkout]");
      if (co) {
        if (PREVIEW) { e.preventDefault(); return; }

        // A CHECKOUT REPLACEMENT OWNS THIS CLICK.
        //
        // Five of these stores check out through Lasso. Reading Lasso's own script settles
        // how it hooks a cart drawer:
        //
        //   document.addEventListener("click", e => e.composedPath().some(el =>
        //     el?.matches?.('[class*="upcart-checkout"], [class*="opus-btn-checkout"],
        //                   [class*="slide-cart-checkout"]')) && proceedToCheckout(), true)
        //
        // A hardcoded list of three competitors' class names. No cart of ours will ever be on
        // it, and giving our button their class would be dressing up as Upcart — it would work
        // until the day it did not, on the part of the shop that takes the money. Lasso also
        // publishes window.proceedToCheckout, which is the same call its own handler ends in,
        // so we ask for the replacement checkout by name instead of impersonating our way into
        // it. A store without one just follows the link.
        var replaced = typeof window.proceedToCheckout === "function";
        if (replaced) {
          e.preventDefault();
          return window.proceedToCheckout();
        }
        return;   // plain Shopify checkout: let the browser follow the anchor
      }
    });

    r.addEventListener("submit", function (e) {
      if (!e.target.closest("[data-ec-code-form]")) return;
      e.preventDefault();
      if (PREVIEW) return;
      var input = e.target.querySelector(".ec-code-input");
      var code = (input && input.value || "").trim();
      if (!code) return;

      // /discount/CODE is still how a code gets onto a cart — Shopify has no Ajax endpoint
      // for it — but it does NOT have to be a navigation. It was, and a customer who mistyped
      // a code was thrown out of the drawer onto the full cart page with no explanation. My
      // own comment here claimed Upcart did the same; I never checked, and it does not — the
      // entire point of a drawer is that you stay in it. Fetching the URL sets the same cookie
      // without moving the page, and the cart is then re-read to see whether anything landed.
      // e.target IS the form. `f` was a leftover from the version before this one and does
      // not exist here — the handler threw a ReferenceError on this line, after
      // preventDefault had already run. So the drawer stayed open and looked like it had
      // handled the code, while the request was never sent at all: a VALID code did nothing
      // either. It read as "codes do not work on this store" rather than as a crash, which is
      // why it survived a test that only checked the customer had not been navigated away.
      var btn = e.target.querySelector(".ec-code-btn");
      if (btn) btn.disabled = true;
      codeError = false;
      codeTyped = code;
      fetch(routed("/discount/") + encodeURIComponent(code), { credentials: "same-origin" })
        .then(function () { return fetchCart(); })
        .then(function () {
          var got = (CART.cart_level_discount_applications || []).some(function (d) {
            return String(d.title || "").toUpperCase() === code.toUpperCase();
          });
          codeError = !got;
          if (got) codeTyped = "";        // it worked; the field can empty itself
          paint();
        })
        .catch(function () { codeError = true; paint(); });
    });

    r.addEventListener("input", function (e) {
      if (e.target.classList && e.target.classList.contains("ec-code-input")) {
        codeTyped = e.target.value;
        if (codeError) { codeError = false; var p = r.querySelector(".ec-code-err"); if (p) p.remove(); }
      }
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && open) setOpen(false);
    });
  }

  /* ── Interception ───────────────────────────────────────────────────────────
     The whole point of a cart drawer: the customer clicks the theme's own Add to
     cart and OUR drawer opens. Three routes in, because themes use all three. */

  function interceptForms() {
    document.addEventListener("submit", function (e) {
      var f = e.target;
      if (!f || !f.action || f.getAttribute("data-ec-ignore")) return;
      if (f.action.indexOf("/cart/add") === -1) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var fd = new FormData(f);
      fetch(routed("/cart/add.js"), { method: "POST", body: fd, credentials: "same-origin",
                                      headers: { "Accept": "application/json" } })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (j && j.status && j.status >= 400) throw (j.description || j.message);
          if (CFG.behaviour.open_on_add) { pendingAdd = true; openDrawer(); } else refresh();
        })
        .catch(function (err) {
          // Sold out / not enough stock: say so where the customer is looking, then
          // let the theme's own cart take over rather than swallowing the click.
          if (window.console) console.warn("[Eight Cart] add failed", err);
          window.alert(typeof err === "string" ? err : "Sorry, that item could not be added.");
        });
    }, true);     // capture: get there before the theme's own handler
  }

  // Themes that add via their own fetch/XHR never fire a submit we can see. Patch the
  // two transports and react to what actually happened, rather than guessing.
  function interceptXHR() {
    var origFetch = window.fetch;
    if (origFetch) {
      window.fetch = function (input, init) {
        var url = typeof input === "string" ? input : (input && input.url) || "";
        var mine = url.indexOf("/cart/add") > -1 || url.indexOf("/cart/change") > -1 ||
                   url.indexOf("/cart/update") > -1 || url.indexOf("/cart/clear") > -1;
        var isAdd = url.indexOf("/cart/add") > -1;
        var p = origFetch.apply(this, arguments);
        if (!mine || url.indexOf("ec-self") > -1) return p;
        return p.then(function (res) {
          if (res && res.ok) {
            setTimeout(function () {
                  if (isAdd && CFG.behaviour.open_on_add && !open) { pendingAdd = true; openDrawer(); } else refresh();
            }, 0);
          }
          return res;
        });
      };
    }
    var open_ = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__ecUrl = String(url || "");
      return open_.apply(this, arguments);
    };
    var send_ = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      var self = this;
      this.addEventListener("load", function () {
        var u = self.__ecUrl || "";
        if (u.indexOf("/cart/") === -1 || self.status >= 400) return;
        var isAdd = u.indexOf("/cart/add") > -1;
        if (isAdd && CFG.behaviour.open_on_add && !open) { pendingAdd = true; openDrawer(); } else refresh();
      });
      return send_.apply(this, arguments);
    };
  }

  function interceptCartLinks() {
    if (!CFG.behaviour.intercept_cart_link) return;
    document.addEventListener("click", function (e) {
      var a = e.target.closest && e.target.closest(
        'a[href$="/cart"], a[href*="/cart?"], [href$="/cart"], [data-ec-open],' +
        // Named triggers: plenty of themes hang the drawer off a class, not an href, and
        // Impulse (LUMA, Grant & Morgan) is one of them.
        '.js-drawer-open-cart, .site-header__cart, [data-cart-toggle], [data-drawer-toggle="cart"],' +
        '.cart-toggle, .header__icon--cart, .js-cart-trigger');
      if (!a) return;
      if (a.closest(".ec-root")) return;
      e.preventDefault();
      e.stopPropagation();
      openDrawer();
    }, true);
  }

  // Some themes open their own drawer from their own handler that we cannot cancel, so the
  // customer ends up looking at two carts. Two nets, because either alone is not enough:
  //
  //   1. the named list — exact, instant, and only covers themes somebody has already seen;
  //   2. a sweep — anything cart-shaped that is covering the page while our drawer is open.
  //
  // The sweep is what makes this survive a theme nobody has tested. It is deliberately
  // narrow (fixed/sticky position, drawer-sized, "cart" in its own id/class) so it cannot
  // eat a sticky header, and everything it touches is restored the moment we close.
  var HIDDEN = [];

  function suppressThemeDrawers() {
    var sel = (CFG.behaviour.hide_selectors || []).join(",");
    if (!sel) return;
    var css = document.createElement("style");
    css.textContent = "body.ec-open :is(" + sel + "){display:none !important;visibility:hidden !important}";
    document.head.appendChild(css);
  }

  // Named by what it is, not only by "cart": plenty of themes call the same element a
  // drawer, a flyout or an off-canvas panel. Anything fixed, drawer-sized and on screen
  // while the customer is looking at their cart is in the way, whatever it is called.
  var SWEEP_WORDS = /(cart|basket|drawer|slideout|slide-out|flyout|fly-out|offcanvas|off-canvas|minicart)/i;
  // …except the things it would be wrong to hide. A consent banner is a legal obligation,
  // not decoration, and it is not ours to suppress for the customer's convenience.
  var SWEEP_NEVER = /(cookie|consent|gdpr|ccpa|privacy|age-?gate|age-?verif)/i;

  // Targeted, not exhaustive. This used to be `[class],[id]` — every element on the page —
  // with a getComputedStyle() and a getBoundingClientRect() on each match. On a real theme
  // that is thousands of forced layouts, and it ran five times in the first second: exactly
  // on top of the opening animation. That, not the transition, is what made it stutter.
  var SWEEP_SELECTOR = 'cart-drawer,cart-notification,' +
    '[class*="cart" i],[id*="cart" i],[class*="drawer" i],[id*="drawer" i],' +
    '[class*="basket" i],[class*="flyout" i],[class*="offcanvas" i],[class*="slideout" i]';

  function sweepThemeDrawers() {
    if (!open || PREVIEW) return;
    var cands = document.querySelectorAll(SWEEP_SELECTOR);
    for (var i = 0; i < cands.length; i++) {
      var n = cands[i];
      if (n === hostEl || (hostEl && (hostEl.contains(n) || n.contains(hostEl)))) continue;
      if (n.tagName === "BODY" || n.tagName === "HTML" || n.tagName === "FORM" ||
          n.tagName === "SCRIPT" || n.tagName === "STYLE") continue;
      if (n.getAttribute("data-ec-keep") !== null) continue;
      var name = (n.id || "") + " " + (n.getAttribute("class") || "") + " " + n.tagName;
      if (!SWEEP_WORDS.test(name) || SWEEP_NEVER.test(name)) continue;
      var cs = window.getComputedStyle(n);
      if (cs.position !== "fixed" && cs.position !== "sticky") continue;
      if (cs.display === "none" || cs.visibility === "hidden" || parseFloat(cs.opacity) === 0) continue;
      var r = n.getBoundingClientRect();
      // Drawer-sized, and actually on screen: not a header strip, not a badge, and not a
      // panel that is parked off-canvas waiting to be opened.
      if (r.width < 200 || r.height < 200) continue;
      // Skip panels parked off-canvas waiting to be opened — but only when the viewport
      // reports a real width. A backgrounded or zero-size tab reports 0, and this check
      // would then throw away every candidate on the page.
      if (window.innerWidth > 0 && (r.right <= 0 || r.left >= window.innerWidth)) continue;
      HIDDEN.push([n, n.style.cssText]);
      n.style.setProperty("display", "none", "important");
    }
  }

  function restoreThemeDrawers() {
    for (var i = 0; i < HIDDEN.length; i++) HIDDEN[i][0].style.cssText = HIDDEN[i][1];
    HIDDEN = [];
  }

  /* ── Cart page takeover ─────────────────────────────────────────────────────
     Optional and off by default: on /cart, open the drawer instead of showing the page.
     A customer who lands on /cart from an email should still see a cart, so we only do
     this when the merchant asks for it. */
  function maybeTakeOverCartPage() {
    if (!CFG.behaviour.replace_cart_page) return;
    if (window.location.pathname.replace(/\/$/, "").indexOf("/cart") === -1) return;
    if (window.location.pathname.indexOf("/cart/") > -1) return;    // /cart/change etc.
    openDrawer();
  }

  /* ── Boot ───────────────────────────────────────────────────────────────────── */

  function init(cfg, shop) {
    if (cfg) CFG = merge(DEFAULTS, cfg);
    if (shop) SHOP = shop;
    if (!CFG.enabled && !PREVIEW) return;      // installed but switched off = truly inert
    ensureRoot();
    bindRoot();
    if (!PREVIEW) {
      interceptForms();
      interceptXHR();
      interceptCartLinks();
      suppressThemeDrawers();
      mountSticky();
      fetchCart().then(function () {
        paint();
        updateStickyCount();
        maybeTakeOverCartPage();
        // The customer already asked for the cart before we existed — honour it rather than
        // swallowing the click that the early guard cancelled on our behalf.
        var early = window.__eightCartEarly;
        if (early && early.form) {
          var f = early.form; early.form = null;
          var fd = new FormData(f);
          fetch(routed("/cart/add.js"), { method: "POST", body: fd, credentials: "same-origin",
                                          headers: { "Accept": "application/json" } })
            .then(function () { pendingAdd = true; openDrawer(); })
            .catch(function () { openDrawer(); });
        } else if (early && early.open) {
          early.open = false;
          openDrawer();
        }
      }).catch(function () { paint(); });
    }
  }

  /* Public surface. `preview` is what the cockpit editor drives: it hands over a
     config and a fake cart and gets the real drawer, with no network and no hijacking. */
  window.EightCart = {
    version: "1.1.0",
    defaults: DEFAULTS,
    init: init,
    open: openDrawer,
    close: function () { setOpen(false); },
    refresh: refresh,
    render: render,
    money: money,
    merge: merge,
    preview: function (container, cfg, cart, shop) {
      PREVIEW = true;
      window.EightCartPreview = true;
      // The preview must speak the store's own currency and carry its own name, or a
      // euro store is designed against dollars and the header reads "Cart" for everyone.
      if (shop) SHOP = shop;
      CFG = merge(DEFAULTS, cfg || {});
      CART = cart || CART;
      recount();
      // Shadow here too. The whole promise of this preview is that it is the same drawer
      // the customer gets; rendering it in the light DOM while the storefront renders it
      // shadowed would quietly make that untrue.
      hostEl = container;
      if (!container.shadowRoot && container.attachShadow) {
        shadow = container.attachShadow({ mode: "open" });
        if (shop && shop.css_url) {
          var l = document.createElement("link"); l.rel = "stylesheet"; l.href = shop.css_url;
          shadow.appendChild(l);
        }
        // .ec-root, the same class the storefront's own root carries. Without it the
        // preview's shadow tree had a classless wrapper, so every rule written as
        // `.ec-root .something` matched live and silently did nothing here — the class sits
        // on the HOST, outside the shadow, where CSS inside cannot see it. That is what left
        // the checkout button half width in the editor while the shop had it full width, and
        // it got worse the moment I added .ec-root to the rule to "fix" it.
        root = document.createElement("div");
        root.className = "ec-root";
        shadow.appendChild(root);
      } else if (container.shadowRoot) {
        shadow = container.shadowRoot;
        root = shadow.querySelector(".ec-root") || root;
      } else {
        // No shadow support (old browser, or a test harness): render straight into the
        // container. Isolation is lost, the drawer is not.
        shadow = null;
        root = container;
      }
      root.setAttribute("style", cssVars(CFG.design));
      root.className = "ec-root is-visible is-open is-preview";
      // The preview is inert on purpose — no checkout, no quantity steps — except for the
      // protection switch: the owner has to be able to see both of its states to judge the
      // colour he just picked. It flips a local flag and redraws; no cart is touched.
      if (root.addEventListener && !root.__ecProtBound) {   // a test stub has no listeners
        root.__ecProtBound = true;
        root.addEventListener("click", function (e) {
          var pt = e.target && e.target.closest && e.target.closest("[data-ec-prot-toggle]");
          if (!pt) return;
          e.preventDefault();
          protPreviewOn = !protPreviewOn;
          lastHtml = null;
          root.innerHTML = render();
        });
      }
      // The editor calls this on every keystroke with a whole new config, so the markup on
      // screen is no longer whatever paint() last recorded. Clearing the record keeps a later
      // paint from deciding there is nothing to do.
      lastHtml = null;
      root.innerHTML = render();
      drawerEl = root.querySelector(".ec-drawer");
      startAnnouncements();
      return container;
    },
    // Exposed for the test suite and for measuring on a live shop: this is the function
    // that decides whether the drawer is updated or rebuilt, and three bugs came from
    // getting that wrong.
    _morph: morph,
    _state: function () { return { cfg: CFG, cart: CART }; }
  };

  // In the theme the snippet calls init() itself once Liquid has written the config,
  // so we do not auto-boot here — that would race the config into an empty default.
})(window, document);
